import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Plus, X, FileText, Sparkles, Trash2, Hand, GraduationCap, Sun, CalendarDays, Trophy, UserRound, Flame, ExternalLink, Receipt, Ticket } from "lucide-react";
import { api } from "../lib/data";
import { usePlanning } from "../lib/usePlanning";
import { seatState, courseSlug, durationLabel, MESSAGE_ESSAI_COMPLET } from "../lib/planningHelpers";
import { STAGES, stageFerme, PARAMETRES, CARTE_10, carte10Eligible, detailAccumulation, buildCourseOptions, computeFamilyPricing, PASSES, isPassEligibleForChild, findBetterPass, courseOptionLabel, isDureeTarifee } from "../lib/pricing";
import { messageInscriptionTardive, inscriptionPourLaRentree, MESSAGE_INSCRIPTION_TARDIVE, LABEL_PLACES_RENTREE, estEnPortesOuvertes, prochaineFenetre, libelleFenetre, participeAuSpectacle, fenetreEssaisSuspendus, essaiSuspendu, suspensionEssaisActive, appliquerBasculeSpectacle, libelleBasculeSpectacle, libelleVeilleBasculeSpectacle } from "../lib/dates";
import { useReglages } from "../lib/useReglages";
import { useChantiers } from "../lib/useChantiers";
import { isAdultForTarifs, birthDateError, ageWarningForCourse, ageAt } from "../lib/ages";
import { forfaitSessions, FORFAIT_SUBLABEL } from "../lib/forfaits";
import { CalendarPicker, FR_DAY_TO_IDX, buildICS, downloadICS } from "../components/CalendarPicker";
import { VACANCES_SCOLAIRES } from "../lib/vacancesScolaires";
import { AUDITION, auditionPassee } from "../lib/auditions";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { formatSwissPhone, isValidSwissPhone, cityFromNpa, isValidSwissNpa, isAutoFilledCity, isValidEmail } from "../lib/formHelpers";
import { ChampNaissance } from "../components/ChampNaissance";
import { EmailSuggestion } from "../components/EmailSuggestion";
import { formatAvs, erreurAvs, AVS_PLACEHOLDER } from "../lib/avs";
import { useFamilleConnectee, coordonneesDe } from "../lib/familleConnectee";

// Une famille connectée à son espace : on le lui dit, et ses coordonnées sont déjà là.
const BandeauConnecte = ({ moi, texte }) => (moi ? (
  <div className="rounded-2xl px-4 py-3 text-sm flex items-start gap-2" data-testid="inscription-connectee"
    style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534" }}>
    <Check size={16} className="shrink-0 mt-0.5" />
    <span>Connecté·e en tant que <strong>{moi.responsable || moi.email}</strong>. {texte}</span>
  </div>
) : null);

// Les élèves déjà connus de l'école : un tap, et prénom, nom, date de naissance et AVS
// sont remplis.
const ElevesConnus = ({ moi, choisis, onChoisir }) => {
  if (!moi?.enfants?.length) return null;
  return (
    <section className="card-flat" data-testid="inscription-eleves-connus">
      <div className="font-display text-xl">Pour qui ?</div>
      <p className="text-sm text-[var(--muted)] mt-1 mb-4">Touche un prénom : sa fiche se remplit toute seule.</p>
      <div className="flex flex-wrap gap-2">
        {moi.enfants.map((e) => {
          const actif = choisis.includes(e.id);
          return (
            <button key={e.id} type="button" onClick={() => onChoisir(e)}
              className="px-4 min-h-[44px] rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
              data-testid={`inscription-eleve-connu-${e.id}`}
              style={actif ? { background: "var(--primary)", color: "#fff" } : { background: "var(--primary-tint)" }}>
              {actif && <Check size={14} />} {e.prenom}
            </button>
          );
        })}
      </div>
    </section>
  );
};

// Les quatre canaux suivis par l'école (console : « D'où viennent les inscriptions »).
const SOURCES_CONNAISSANCE = ["Bouche à oreille", "Instagram", "Google",
  "Événement / spectacle / portes ouvertes", "Autre"];

const REGLEMENT_COMPETITION_URL = "/docs/reglement-competition-compagnie.pdf";

// Un formulaire qui échoue au niveau du réseau (proxy, 4G qui saute, serveur qui redémarre)
// renvoyait une page d'erreur technique en anglais, illisible. Et comme la demande est
// enregistrée AVANT l'envoi des e-mails, elle peut très bien être passée : on doit donc
// dire de ne pas renvoyer le formulaire à l'aveugle plutôt que d'afficher « réessayez ».
function messageErreurEnvoi(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail && detail.length < 300) return detail;
  return (
    "L'envoi n'a pas pu aboutir correctement. Ta demande a peut-être quand même été " +
    "enregistrée : ne renvoie pas le formulaire tout de suite. Écris-nous à " +
    "info1000arts@gmail.com (ou 079 950 40 31) et nous vérifions immédiatement."
  );
}


// =========================================================
// Type selector
// =========================================================
function TypeSelector({ onSelect }) {
  // Spectacles de juin 2027 (version de test d'abord) : leurs billets aussi depuis Inscription.
  const chantiers = useChantiers();
  const TYPES = [
    { key: "essai", label: "Cours d'essai", desc: "Réserve ton créneau et viens tester avant de t'engager.", icon: Hand },
    { key: "semestriel", label: "Cours semestriel", desc: "Inscription au semestre — un ou plusieurs cours, un ou plusieurs enfants.", icon: GraduationCap },
    { key: "stage", label: "Stage", desc: "Inscription à un stage thématique pendant les vacances.", icon: Sun },
  ];
  return (
    <div data-testid="inscription-type-selector" className="space-y-6">
      <div>
        <div className="eyebrow mb-2">Étape 1</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Type de demande</h2>
        <p className="text-[var(--muted)] mt-3">Choisis le type d'inscription qui te correspond.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {TYPES.map((t) => (
          <button
            key={t.key}
            data-testid={`type-${t.key}`}
            onClick={() => onSelect(t.key)}
            className="card text-left hover:border-[var(--primary)]"
            style={{ cursor: "pointer" }}
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
              <t.icon size={22} />
            </div>
            <div className="font-display text-xl">{t.label}</div>
            <p className="text-sm text-[var(--muted)] mt-2">{t.desc}</p>
            <div className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
              Choisir <ArrowRight size={14} />
            </div>
          </button>
        ))}
      </div>

      {/* Formulaires spéciaux — routes dédiées */}
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <Link to="/inscription/audition" data-testid="type-audition" className="card text-left hover:border-[var(--primary)]" style={{ cursor: "pointer" }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
            <Trophy size={22} />
          </div>
          <div className="font-display text-xl">Audition Compétition / Compagnie</div>
          <p className="text-sm text-[var(--muted)] mt-2">{auditionPassee()
            ? "Inscriptions fermées : la prochaine date sera mise en ligne dès que possible."
            : `Casting du ${AUDITION.date_label.replace(/^\S+\s/, "")} — inscription obligatoire avant tout cours Compétition ou Compagnie.`}</p>
          <div className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
            Choisir <ArrowRight size={14} />
          </div>
        </Link>
        <Link to="/inscription/spectacle" data-testid="type-spectacle" className="card text-left hover:border-[var(--primary)]" style={{ cursor: "pointer" }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
            <Ticket size={22} />
          </div>
          <div className="font-display text-xl">Spectacle</div>
          {/* Une seule entrée pour les spectacles (Jen, 30.10.2026) : on y choisit Noël ou juin. */}
          <p className="text-sm text-[var(--muted)] mt-2">{chantiers.spectacles_juin
            ? "Spectacle de Noël (entrée libre, réserve ta place) ou spectacles de juin 2027 (billets en ligne) : tu choisis à l'étape suivante."
            : "Réserve tes places pour le spectacle de Noël — entrée libre, places limitées."}</p>
          <div className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
            Choisir <ArrowRight size={14} />
          </div>
        </Link>
        <Link to="/inscription/cours-prive" data-testid="type-cours-prive" className="card text-left hover:border-[var(--primary)]" style={{ cursor: "pointer" }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
            <UserRound size={22} />
          </div>
          <div className="font-display text-xl">Cours privé (danse, chant, comédie)</div>
          <p className="text-sm text-[var(--muted)] mt-2">Réserve directement un créneau libre dans le calendrier de l'école.</p>
          <div className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
            Choisir <ArrowRight size={14} />
          </div>
        </Link>
        {/* Formulaire séparé depuis le 08.10.2026 (décision de l'école) : seulement les cours
            sans engagement semestriel, une carte par cours, payée à l'école. */}
        <Link to="/inscription/carte-10" data-testid="type-carte10" className="card text-left hover:border-[var(--primary)]" style={{ cursor: "pointer" }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
            <Ticket size={22} />
          </div>
          <div className="font-display text-xl">Carte {CARTE_10.nb_seances} cours</div>
          <p className="text-sm text-[var(--muted)] mt-2">Sans engagement semestriel ({[...CARTE_10.disciplines, ...CARTE_10.mots_cles.map((m) => m.charAt(0).toUpperCase() + m.slice(1))].join(", ")}) : {CARTE_10.nb_seances} séances par cours, CHF {CARTE_10.prix}.– la carte, à régler à l'école.</p>
          <div className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
            Choisir <ArrowRight size={14} />
          </div>
        </Link>
        <Link to="/inscription/workshop" data-testid="type-workshop-heels" className="card text-left hover:border-[var(--primary)]"
          style={{ cursor: "pointer" }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
            <Flame size={22} />
          </div>
          <div className="font-display text-xl">Workshops Heels Technique &amp; Freestyle</div>
          <p className="text-sm text-[var(--muted)] mt-2">Sessions ponctuelles pour perfectionner ta technique et libérer ton freestyle. À l'unité ou au forfait du semestre.</p>
          <div className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
            Choisir <ArrowRight size={14} />
          </div>
        </Link>
      </div>
    </div>
  );
}

// =========================================================
// COURS D'ESSAI FORM
// =========================================================
// PORTES OUVERTES — période pendant laquelle les cours d'essai sont GRATUITS.
// Dates et prix de l'essai saisis par l'école dans le classeur (onglet Listes, colonnes
// AA/AB et AD), lus par `useReglages()`. Aucune date n'est écrite dans le site.

// VACANCES SCOLAIRES & JOURS FÉRIÉS — dates grisées dans le calendrier (pas de cours).
// Source : calendrier officiel DEF Vaud 2023-2031 → /app/frontend/src/lib/vacancesScolaires.js

function EssaiForm({ onBack, preselectedCourse }) {
  const { courses: planningCourses, loading: planningLoading } = usePlanning();
  const { portes_ouvertes: fenetresPO, prix_essai: prixEssai, date_limite_spectacle: basculeSpectacle } = useReglages();
  // Bascule spectacle réglée dans la console (JJ-MM) quand `/api/reglages` la fournit ;
  // sinon le 1er février reste en place. Tous les textes ci-dessous lisent cette date.
  appliquerBasculeSpectacle(basculeSpectacle);
  const debutSpectacle = libelleBasculeSpectacle();
  const prochainePO = prochaineFenetre(fenetresPO);
  const prixEssaiLabel = prixEssai ? `CHF ${prixEssai}.–` : "CHF 10.–";
  const [form, setForm] = useState({
    is_adult_self: false, // NOUVEAU : true = adulte s'inscrit pour lui-même
    student_first_name: "", student_last_name: "", birth_date: "",
    parent_first_name: "", parent_last_name: "", parent_email: "", parent_phone: "",
    parent_address: "", parent_npa: "",
    first_registration: false,
    // reservations: [{ id, name, day, start, end, room, date }] — panier de créneaux réservés
    reservations: [], comments: "",
    accept_one_trial: false, accept_paid_trial: false, accept_confirmation: false,
  });
  const famille = useFamilleConnectee();
  useEffect(() => {
    if (!famille) return;
    const c = coordonneesDe(famille);
    setForm((f) => ({
      ...f,
      parent_first_name: f.parent_first_name || c.prenom, parent_last_name: f.parent_last_name || c.nom,
      parent_email: f.parent_email || c.email, parent_phone: f.parent_phone || formatSwissPhone(c.telephone),
      parent_address: f.parent_address || c.adresse, parent_npa: f.parent_npa || c.npa,
    }));
  }, [famille]);
  const [pendingCourse, setPendingCourse] = useState(null); // {id, name, day, start, end, room}
  // Cours complet arrivé par un lien (planning, vieux favori) : pas d'essai, on propose la
  // liste d'attente à la place (école, 02.11.2026).
  const [coursComplet, setCoursComplet] = useState(null);
  const [pendingDate, setPendingDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Après janvier, certains cours ne prennent plus d'essais (préparation spectacle).
  // Avertissement général affiché seulement pendant la fenêtre réelle de suspension
  // (1er février → veille des Portes Ouvertes de juin). Le reste de l'année, tous les cours
  // acceptent les essais : inutile de décourager une famille qui s'inscrit en septembre.
  const noTrialForSpectacle = suspensionEssaisActive(fenetresPO);

  // Le formulaire propose les cours actifs du planning (Google Sheet), SAUF :
  //  - les cours dont ETAT_PLACES = FERME (non réservables)
  //  - les groupes Compétition et Compagnie : l'accès se fait par audition puis
  //    inscription au semestre, jamais par un cours d'essai.
  // Un cours COMPLET reste dans la liste, grisé : pas d'essai tant qu'il est complet
  // (école, 02.11.2026), la famille voit pourquoi.
  const availableCourses = useMemo(() => (planningCourses || [])
    .filter((c) => !seatState(c).hidden && c.discipline !== "Compétition")
    .map((c) => ({
      id: c.id,
      label: `${c.day} ${c.start} — ${c.name} · ${c.room}${c.level ? ` · Niveau ${c.level}` : ""}`,
      day: c.day, start: c.start, end: c.end, name: c.name, room: c.room,
      age_label: c.age_label, duration_min: c.duration_min, level: c.level,
      etat_places: c.etat_places, message_site: c.message_site,
      participation_spectacle: c.participation_spectacle,
      slug: courseSlug(c),
      complet: !!seatState(c).waitlist,
    })), [planningCourses]);

  // Pré-sélection depuis l'URL (?course=slug ou ?course=<id>) — venu du planning.
  React.useEffect(() => {
    if (!preselectedCourse || pendingCourse || coursComplet || availableCourses.length === 0) return;
    const match = availableCourses.find(
      (c) => c.id === preselectedCourse || c.slug === preselectedCourse
    );
    if (match?.complet) setCoursComplet(match);
    else if (match) setPendingCourse(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedCourse, availableCourses]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  const addCourse = (id) => {
    const opt = availableCourses.find((c) => c.id === id);
    if (!opt) return;
    if (opt.complet) { setCoursComplet(opt); return; }
    setCoursComplet(null);
    setPendingCourse(opt);
    setPendingDate("");
  };
  const removeReservation = (idx) => setForm((f) => ({ ...f, reservations: f.reservations.filter((_, i) => i !== idx) }));
  const confirmReservation = () => {
    if (!pendingCourse || !pendingDate) return;
    // Avoid duplicates (same course + same date)
    const key = `${pendingCourse.id}|${pendingDate}`;
    setForm((f) => f.reservations.some((r) => `${r.id}|${r.date}` === key) ? f : ({
      ...f,
      reservations: [...f.reservations, { ...pendingCourse, date: pendingDate }],
    }));
    setPendingCourse(null);
    setPendingDate("");
  };
  const cancelPending = () => { setPendingCourse(null); setPendingDate(""); };

  const canSubmit =
    // Si adulte : parent_* = ses propres infos, pas besoin de student_*
    // Si enfant : besoin des infos enfant + parent
    (form.is_adult_self
      ? (form.parent_first_name && form.parent_last_name && isValidEmail(form.parent_email) && form.parent_phone)
      : (form.student_first_name && form.student_last_name && form.birth_date &&
         form.parent_first_name && form.parent_last_name && isValidEmail(form.parent_email) && form.parent_phone)) &&
    form.reservations.length > 0 &&
    form.accept_one_trial;

  // Beaucoup de visiteurs choisissaient une date puis cliquaient sur « Envoyer ma demande »
  // sans confirmer le cours : le bouton grisé ne disait pas pourquoi. On liste ce qui manque.
  const manquants = [];
  if (!form.is_adult_self) {
    if (!form.student_first_name || !form.student_last_name) manquants.push("le prénom et le nom de l'élève");
    if (!form.birth_date) manquants.push("la date de naissance de l'élève");
  }
  if (!form.parent_first_name || !form.parent_last_name) {
    manquants.push(form.is_adult_self ? "ton prénom et ton nom" : "le prénom et le nom du représentant légal");
  }
  if (!isValidEmail(form.parent_email)) manquants.push("une adresse e-mail valide");
  if (!form.parent_phone) manquants.push("un numéro de téléphone");
  if (form.reservations.length === 0) {
    manquants.push(pendingCourse
      ? "confirmer ton cours d'essai avec le bouton « Confirmer ce cours d'essai » ci-dessus"
      : "choisir au moins un cours d'essai et sa date");
  }
  if (!form.accept_one_trial) manquants.push("cocher la case de confirmation");

  const sending = useRef(false);
  const submit = async () => {
    if (sending.current) return;
    // Double-check côté client : email valide sinon on bloque avec un message clair
    if (!isValidEmail(form.parent_email)) {
      setError("Merci de vérifier l'adresse e-mail — elle ne semble pas valide (ex : prenom.nom@exemple.ch).");
      return;
    }
    sending.current = true;
    setLoading(true); setError("");
    try {
      // Si adulte : on envoie ses infos comme infos "élève" pour compat backend
      const payload = form.is_adult_self
        ? {
            ...form,
            parent_name: `${form.parent_first_name} ${form.parent_last_name}`.trim(),
            student_first_name: form.parent_first_name,
            student_last_name: form.parent_last_name,
            courses: form.reservations,
          }
        : { ...form, parent_name: `${form.parent_first_name} ${form.parent_last_name}`.trim(), courses: form.reservations };
      await api.post("/inscriptions/essai", payload);
      setSuccess(true);
    } catch (e) {
      setError(messageErreurEnvoi(e));
    } finally { setLoading(false); sending.current = false; }
  };

  if (success) {
    const addAllToCalendar = () => {
      form.reservations.forEach((c, i) => {
        const ics = buildICS({
          title: `Cours d'essai — ${c.name} · 1000 ARTS`,
          date: c.date,
          start: c.start,
          end: c.end,
          description: `Cours d'essai chez 1000 ARTS.\nÉlève : ${form.student_first_name} ${form.student_last_name}\nSalle ${c.room}\nAdresse : Chemin des Dailles 10E, 1053 Cugy VD\nContact : +41 79 950 40 31 · info1000arts@gmail.com`,
          location: "1000 ARTS · Chemin des Dailles 10E, 1053 Cugy VD, Suisse",
        });
        setTimeout(() => downloadICS(ics, `essai-${c.name.replace(/\s+/g,"-").toLowerCase()}-${c.date}.ics`), i * 300);
      });
    };
    return (
      <div data-testid="essai-success" className="py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "var(--primary)" }}>
            <Check size={28} color="#fff" />
          </div>
          <h2 className="font-display text-3xl md:text-4xl">Merci, ta demande a bien été envoyée !</h2>
          <p className="text-[var(--muted)] mt-4 max-w-lg mx-auto">
            1000 ARTS te confirmera très bientôt ton cours d'essai par e-mail à <strong>{form.parent_email}</strong>.
          </p>
        </div>

        <section className="card-flat max-w-2xl mx-auto" data-testid="essai-recap">
          <div className="font-display text-xl mb-4">Récapitulatif de ta réservation</div>
          <div className="space-y-3">
            {form.reservations.map((c, idx) => {
              const d = new Date(c.date+"T00:00:00");
              const isPO = estEnPortesOuvertes(c.date, fenetresPO);
              return (
                <div key={idx} className="p-4 rounded-2xl" style={{ background: "var(--primary-tint)" }}>
                  <div className="font-semibold text-[var(--text)]">{c.name}</div>
                  <div className="text-sm mt-1 text-[var(--text)]">
                    📅 {d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  </div>
                  <div className="text-sm text-[var(--muted)]">🕐 {c.start} – {c.end} · Salle {c.room}</div>
                  <div className="text-xs mt-2" style={{ color: isPO ? "#059669" : "#0E7490", fontWeight: 600 }}>
                    {isPO ? "🎉 Portes Ouvertes — GRATUIT" : `ℹ️ ${prixEssaiLabel} sur place`}
                  </div>
                </div>
              );
            })}
          </div>

          <button onClick={addAllToCalendar} data-testid="essai-add-to-calendar" className="btn-primary w-full justify-center mt-6">
            <CalendarDays size={16} /> Ajouter à mon calendrier ({form.reservations.length} {form.reservations.length > 1 ? "cours" : "cours"})
          </button>
          <p className="text-xs text-[var(--muted)] mt-3 text-center">
            💡 Un fichier .ics sera téléchargé pour chaque cours. Ouvre-le pour l'ajouter à Google Calendar, Apple Calendar ou Outlook.
          </p>

          <div className="mt-6 pt-4 border-t border-[var(--border-soft)] text-sm text-[var(--muted)]">
            📍 <strong>1000 ARTS</strong> · Chemin des Dailles 10E, 1053 Cugy VD<br />
            📞 +41 79 950 40 31 · ✉️ info1000arts@gmail.com
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="essai-form" className="space-y-8">
      <button onClick={onBack} data-testid="essai-back" className="btn-ghost"><ArrowLeft size={14} /> Retour</button>

      <div>
        <div className="eyebrow mb-2">Cours d'essai</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Réserve ton essai</h2>
      </div>

      {/* Info Portes Ouvertes */}
      <section className="card-flat" style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
        <div className="flex items-start gap-3">
          <div className="text-2xl">🎉</div>
          <div className="flex-1">
            <div className="font-display text-lg mb-1">
              {prochainePO ? `Portes Ouvertes ${libelleFenetre(prochainePO)}` : "Portes Ouvertes"}
            </div>
            <p className="text-sm text-[var(--text)]">
              Pendant nos <strong>Portes Ouvertes</strong>, les cours d'essai sont <strong>offerts</strong>. En dehors de cette période, un essai est facturé <strong>{prixEssaiLabel}</strong> sur place (Twint ou cash) — ce montant n'est ni remboursé ni déduit de la facture d'inscription. Les cours d'essai suivent le <strong>planning habituel</strong> ci-dessous — c'est 1000ARTS qui te confirmera ton créneau.
            </p>
            {noTrialForSpectacle && (
              <div className="mt-4 p-3 rounded-2xl text-sm" style={{ background: "#FEF9C3", border: "1px solid #FDE68A" }} data-testid="essai-avis-spectacle">
                ⚠️ Les cours qui participent au <strong>spectacle de fin d'année</strong> n'acceptent plus de cours d'essai <strong>dès le {debutSpectacle}</strong> : le groupe répète intensivement. {prochainePO ? <>Prochaine période d'essai pour ces cours : les <strong>Portes Ouvertes {libelleFenetre(prochainePO)}</strong>.</> : <>Prochaine période d'essai pour ces cours : les <strong>Portes Ouvertes de juin</strong>.</>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Toggle Adulte pour lui-même */}
      <section className="card-flat" style={{ borderColor: "var(--primary)", background: "var(--primary-tint)" }} data-testid="essai-adulte-toggle">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 accent-[var(--primary)] w-5 h-5"
            checked={form.is_adult_self}
            onChange={(e) => setForm({ ...form, is_adult_self: e.target.checked })}
            data-testid="essai-is-adult-self"
          />
          <div>
            <div className="font-display text-lg">Je m'inscris pour moi-même (adulte)</div>
            <div className="text-sm text-[var(--muted)] mt-1">Coche cette case si tu es un adulte qui s'inscrit à un cours pour toi (sans passer par un représentant légal).</div>
          </div>
        </label>
      </section>

      <BandeauConnecte moi={famille} texte="Tes coordonnées sont déjà remplies." />

      {/* Élève */}
      {!form.is_adult_self && (
      <section className="card-flat">
        <div className="font-display text-xl mb-5">L'élève</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Prénom *</label>
            <input data-testid="essai-first-name" className="input-field" value={form.student_first_name} onChange={update("student_first_name")} />
          </div>
          <div>
            <label className="field-label">Nom *</label>
            <input data-testid="essai-last-name" className="input-field" value={form.student_last_name} onChange={update("student_last_name")} />
          </div>
          <div>
            <label className="field-label">Date de naissance *</label>
            <ChampNaissance testid="essai-birth" value={form.birth_date} onChange={(v) => setForm((f) => ({ ...f, birth_date: v }))} />
          </div>
        </div>
      </section>
      )}

      {/* Parent OU Adulte lui-même */}
      <section className="card-flat">
        <div className="font-display text-xl mb-5">{form.is_adult_self ? "Vos coordonnées" : "Représentant légal"}</div>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Prénom *</label>
              <input data-testid="essai-parent-first-name" className="input-field" value={form.parent_first_name} onChange={update("parent_first_name")} />
            </div>
            <div>
              <label className="field-label">Nom *</label>
              <input data-testid="essai-parent-last-name" className="input-field" value={form.parent_last_name} onChange={update("parent_last_name")} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="field-label">E-mail *</label>
              <input data-testid="essai-parent-email" type="email" required autoComplete="email" className="input-field" value={form.parent_email} onChange={update("parent_email")} placeholder="prenom.nom@exemple.ch" />
              <EmailSuggestion value={form.parent_email} onFix={(v) => setForm({ ...form, parent_email: v })} testid="essai-email-suggestion" />
            </div>
            <div>
              <label className="field-label">Téléphone *</label>
              <input data-testid="essai-parent-phone" type="tel" inputMode="tel" placeholder="+41 79 950 40 31" className="input-field" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: formatSwissPhone(e.target.value) })} />
            </div>
          </div>
        </div>
      </section>

      {/* Cours + calendrier partagé */}
      <section className="card-flat">
        <div className="font-display text-xl mb-3">Tes cours d'essai</div>
        <p className="text-sm text-[var(--muted)] mb-4">En 3 clics : <strong>1.</strong> choisis un cours dans la liste · <strong>2.</strong> choisis la date dans le calendrier · <strong>3.</strong> clique sur « Confirmer ce cours d'essai ». Tu peux répéter l'opération pour plusieurs cours.</p>

        {/* Panier — cours + dates déjà réservés */}
        {form.reservations.length > 0 && (
          <div className="mb-5 space-y-2">
            {form.reservations.map((r, idx) => {
              const d = new Date(r.date+"T00:00:00");
              const isPO = estEnPortesOuvertes(r.date, fenetresPO);
              return (
                <div key={idx} className="flex items-center justify-between p-3 rounded-2xl gap-3" style={{ background: "var(--primary-tint)", border: "1px solid rgba(20,184,196,0.25)" }} data-testid={`essai-reserved-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{r.name}</div>
                    <div className="text-xs text-[var(--text)] mt-0.5">
                      📅 {d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long" })} · 🕐 {r.start}–{r.end} · {r.room}
                    </div>
                    <div className="text-xs mt-1" style={{ color: isPO ? "#059669" : "#0E7490", fontWeight: 600 }}>
                      {isPO ? "🎉 Gratuit (Portes Ouvertes)" : `ℹ️ ${prixEssaiLabel} sur place`}
                    </div>
                  </div>
                  <button type="button" onClick={() => removeReservation(idx)} data-testid={`essai-reserved-remove-${idx}`} className="text-[var(--muted)] hover:text-red-600 shrink-0"><X size={18} /></button>
                </div>
              );
            })}
          </div>
        )}

        {/* Cours complet (arrivé par un lien) : pas d'essai, la liste d'attente à la place. */}
        {coursComplet && !pendingCourse && (
          <div className="mb-4 p-4 rounded-2xl" data-testid="essai-cours-complet"
            style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="font-display text-lg" style={{ color: "var(--text)" }}>{coursComplet.name}</div>
                <p className="text-sm mt-1 leading-relaxed">{MESSAGE_ESSAI_COMPLET}</p>
              </div>
              <button type="button" onClick={() => setCoursComplet(null)} aria-label="Fermer"
                data-testid="essai-cours-complet-fermer" className="text-[var(--muted)] hover:text-red-600 shrink-0"><X size={18} /></button>
            </div>
            <Link to={`/inscription?type=semestriel&course=${encodeURIComponent(coursComplet.id)}`}
              data-testid="essai-cours-complet-attente" className="btn-primary inline-flex mt-3">
              S'inscrire en liste d'attente <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {/* Sélecteur de cours + calendrier partagé */}
        {!pendingCourse ? (
          <select
            data-testid="essai-course"
            className="input-field"
            defaultValue=""
            onChange={(e) => { if (e.target.value) { addCourse(e.target.value); e.target.value = ""; } }}
          >
            <option value="">
              {planningLoading ? "Chargement du planning…" : `+ ${form.reservations.length > 0 ? "Ajouter un autre cours" : "Sélectionner un cours"}`}
            </option>
            {availableCourses.map((c) => (
              <option key={c.id} value={c.id} disabled={c.complet}>
                {c.label}{c.complet ? " — COMPLET (pas d'essai)" : ""}
              </option>
            ))}
          </select>
        ) : (
          <div className="p-4 rounded-2xl" style={{ background: "var(--primary-tint)", border: "1px solid rgba(20,184,196,0.3)" }}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1">
                <div className="font-display text-lg">{pendingCourse.name}</div>
                <div className="text-sm text-[var(--muted)]">📅 {pendingCourse.day} · {pendingCourse.start}–{pendingCourse.end} · {pendingCourse.room} {durationLabel(pendingCourse.duration_min)}</div>
                {pendingCourse.message_site && (
                  <div className="text-xs mt-1 font-semibold" data-testid="essai-pending-seats" style={{ color: seatState(pendingCourse).urgent ? "#B45309" : "var(--primary)" }}>
                    {pendingCourse.message_site}
                  </div>
                )}
              </div>
              <button type="button" onClick={cancelPending} data-testid="essai-cancel-pending" className="text-[var(--muted)] hover:text-red-600 shrink-0"><X size={18} /></button>
            </div>
            <div className="field-label mb-2 flex items-center gap-2"><CalendarDays size={14} /> Choisis la date de ton essai *</div>
            {participeAuSpectacle(pendingCourse) && (() => {
              const suspension = fenetreEssaisSuspendus(fenetresPO);
              const reprise = suspension.reprise;
              // Avant la bascule spectacle (1er février par défaut), l'essai est possible : une simple ligne discrète suffit
              // pour expliquer les dates grisées. L'encart rouge n'apparaît que le jour où la
              // restriction s'applique vraiment, et disparaît seul dès les Portes Ouvertes.
              if (!suspensionEssaisActive(fenetresPO)) {
                return (
                  <p className="mb-3 text-xs text-[var(--muted)] italic" data-testid="essai-info-spectacle">
                    Ce cours participe au spectacle de fin d'année : les essais sont possibles jusqu'au {libelleVeilleBasculeSpectacle()}.
                  </p>
                );
              }
              return (
                <div
                  className="mb-3 p-3 rounded-xl text-sm flex items-start gap-2"
                  style={{ background: "#FEE2E2", border: "1px solid #FCA5A5", color: "#7F1D1D" }}
                  data-testid="essai-suspendu-spectacle"
                >
                  <span>🎭</span>
                  <span>
                    Ce cours prépare le <strong>spectacle de fin d'année</strong> : plus aucun cours
                    d'essai n'est possible <strong>dès le {debutSpectacle}</strong> (les dates
                    concernées sont grisées dans le calendrier).{" "}
                    {reprise
                      ? <>Prochaine période d'essai : les <strong>Portes Ouvertes {libelleFenetre(reprise)}</strong>.</>
                      : <>Prochaine période d'essai : les <strong>Portes Ouvertes de juin</strong>.</>}
                  </span>
                </div>
              );
            })()}
            <div className="max-w-md">
              <CalendarPicker
                allowedWeekday={FR_DAY_TO_IDX[pendingCourse.day.toLowerCase()]}
                value={pendingDate}
                onChange={setPendingDate}
                blockedRanges={participeAuSpectacle(pendingCourse) ? [...VACANCES_SCOLAIRES, fenetreEssaisSuspendus(fenetresPO)] : VACANCES_SCOLAIRES}
                poRanges={fenetresPO}
              />
            </div>
            {pendingDate && (() => {
              const d = new Date(pendingDate+"T00:00:00");
              const isPO = estEnPortesOuvertes(pendingDate, fenetresPO);
              return (
                <div className="mt-3 p-3 rounded-xl text-sm" style={{ background: isPO ? "#DCFCE7" : "#E0F7FA", color: isPO ? "#065F46" : "#0E7490", fontWeight: 600 }}>
                  {isPO
                    ? `🎉 Le ${d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} — Portes Ouvertes, essai GRATUIT !`
                    : `ℹ️ Le ${d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} — essai facturé ${prixEssaiLabel} sur place.`}
                </div>
              );
            })()}
            {pendingDate && (
              <div
                className="mt-3 p-3 rounded-xl text-sm flex items-start gap-2 rappel-respire"
                style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E", fontWeight: 600 }}
                data-testid="essai-hint-confirm"
              >
                <span>👇</span>
                <span>Dernière étape : clique sur <strong>« Confirmer ce cours d'essai »</strong> pour l'ajouter à ta demande.</span>
              </div>
            )}
            <button type="button" onClick={confirmReservation} disabled={!pendingDate} data-testid="essai-confirm-reservation" className="btn-primary w-full justify-center mt-4">
              <Plus size={16} /> Confirmer ce cours d'essai
            </button>
          </div>
        )}

        <div className="mt-5">
          <label className="field-label">Commentaire</label>
          <textarea data-testid="essai-comments" rows={3} className="input-field" value={form.comments} onChange={update("comments")} />
        </div>
        <div className="mt-5" data-testid="essai-source-box">
          <label className="field-label">
            Comment as-tu connu 1000 ARTS ? <span className="text-[var(--muted)] font-normal">(optionnel)</span>
          </label>
          <select data-testid="essai-source" className="input-field mt-1"
            value={form.source_connaissance || ""} onChange={update("source_connaissance")}>
            <option value="">Je préfère ne pas répondre</option>
            {SOURCES_CONNAISSANCE.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {form.source_connaissance === "Autre" && (
            <input data-testid="essai-source-precision" className="input-field mt-3"
              placeholder="Dis-nous comment (affiche, passage devant l'école, école, club…)"
              value={form.source_precision || ""} onChange={update("source_precision")} />
          )}
        </div>
      </section>

      {/* Checkboxes */}
      <section className="card-flat">
        <div className="font-display text-xl mb-5">Confirmation</div>
        <div className="space-y-3">
          {[
            { k: "accept_one_trial", txt: "Je comprends qu'un seul cours d'essai est possible par élève et par discipline." },
          ].map((c) => (
            <label key={c.k} className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid={`essai-check-${c.k}`} checked={form[c.k]} onChange={toggle(c.k)} className="mt-1 accent-[var(--primary)]" />
              <span className="text-sm text-[var(--text)]">{c.txt}</span>
            </label>
          ))}
        </div>
      </section>

      {error && <div data-testid="essai-error" className="text-sm" style={{ color: "#DC2626" }}>{error}</div>}

      {!canSubmit && (
        <div
          className="p-4 rounded-2xl text-sm"
          style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}
          data-testid="essai-manquants"
        >
          <div className="font-semibold mb-2">Pour envoyer ta demande, il te reste à&nbsp;:</div>
          <ul className="space-y-1 list-disc pl-5">
            {manquants.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
      )}

      <button data-testid="essai-submit" onClick={submit} disabled={!canSubmit || loading} className="btn-primary w-full justify-center">
        {loading ? "Envoi…" : "Envoyer ma demande"} <ArrowRight size={16} />
      </button>
    </div>
  );
}

// =========================================================
// COURS SEMESTRIEL FORM (multi-enfants + multi-cours + pricing)
// =========================================================
function SemestrielForm({ onBack, preselectedCourse }) {
  const { courses: planningCourses, loading: planningLoading } = usePlanning();
  const [isAdultSelf, setIsAdultSelf] = useState(false); // adulte s'inscrit pour lui-même
  // Les workshops ponctuels (1×/mois) ont leur propre formulaire externe et ne se
  // facturent pas au semestre : on ne les propose pas ici.
  // Un cours dont Début/Fin ne donne pas une durée tarifée (30, 45, 60, 75 ou 90 min) est
  // retiré du choix : mieux vaut une inscription impossible — l'école reçoit une alerte —
  // qu'un montant inventé dans un e-mail de confirmation.
  const COURSE_OPTIONS = useMemo(
    () => buildCourseOptions((planningCourses || []).filter(
      (c) => !seatState(c).hidden && !/1\s*[×x]\s*\/\s*mois/i.test(c.name) && isDureeTarifee(c)
    )),
    [planningCourses]
  );

  // nouvel_eleve est coché par défaut : la grande majorité des inscriptions viennent de
  // familles nouvelles, la taxe administrative est donc annoncée d'entrée de jeu et se
  // décoche pour un élève déjà connu de l'école.
  // photo_consent : le droit à l'image de CET élève (« oui » par défaut, comme l'ancienne
  // réponse unique de la famille).
const blankChild = () => ({ first_name: "", last_name: "", birth_date: "", avs: "", nouvel_eleve: true, comments: "", courses: [], pass_id: "", photo_consent: "oui" });
  // Élève déjà connu (famille connectée) : sa réponse enregistrée reprend la main, un « Non »
  // donné dans l'app ne repasse pas à « Oui » par défaut.
const photoConnue = (v) => (String(v || "").trim().toLowerCase().startsWith("n") ? "non" : "oui");

  const [step, setStep] = useState(1); // 1: enfants, 2: cours, 3: récap, 4: validation
  // Changement d'étape : on remonte en haut du formulaire. Sans cela, après une longue
  // étape 1 on arrivait au milieu (ou en bas) de l'étape suivante. Réappliqué au frame
  // suivant car la hauteur de la page change au moment du rendu de la nouvelle étape.
  useEffect(() => {
    const toTop = () => window.scrollTo(0, 0);
    toTop();
    const raf = requestAnimationFrame(toTop);
    const timers = [setTimeout(toTop, 60), setTimeout(toTop, 250)];
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [step]);
  const [parent, setParent] = useState({ name: "", first_name: "", last_name: "", email: "", phone: "", address: "", npa: "", city: "", first_registration: true, parrain: "" });
  const [children, setChildren] = useState([blankChild()]);
  const famille = useFamilleConnectee();
  useEffect(() => {
    if (!famille) return;
    const c = coordonneesDe(famille);
    setParent((p) => ({
      ...p,
      first_name: p.first_name || c.prenom, last_name: p.last_name || c.nom,
      name: p.name || `${c.prenom} ${c.nom}`.trim(), email: p.email || c.email,
      phone: p.phone || formatSwissPhone(c.telephone), address: p.address || c.adresse,
      npa: p.npa || c.npa, city: p.city || c.localite,
      first_registration: (famille.enfants || []).length ? false : p.first_registration,
    }));
  }, [famille]);
  // Un élève connu choisi d'un tap : il prend la place de la fiche vide, ou s'ajoute.
  const elevesChoisis = (famille?.enfants || [])
    .filter((e) => children.some((c) => c.eleve_connu === e.id)).map((e) => e.id);
  const choisirEleveConnu = (e) => {
    setChildren((arr) => {
      if (arr.some((c) => c.eleve_connu === e.id)) {
        const reste = arr.filter((c) => c.eleve_connu !== e.id);
        return reste.length ? reste : [blankChild()];
      }
      const fiche = { ...blankChild(), eleve_connu: e.id, first_name: e.prenom, last_name: e.nom,
        birth_date: e.date_naissance || "", avs: e.avs || "", nouvel_eleve: false,
        photo_consent: photoConnue(e.droit_image) };
      const vide = arr.findIndex((c) => !c.first_name && !c.last_name && !c.courses.length);
      if (vide >= 0) {
        // La fiche vide garde les cours déjà choisis depuis le planning.
        return arr.map((c, i) => (i === vide ? { ...fiche, courses: c.courses } : c));
      }
      const libre = arr.findIndex((c) => !c.first_name && !c.last_name);
      if (libre >= 0) return arr.map((c, i) => (i === libre ? { ...fiche, courses: c.courses } : c));
      return [...arr, fiche];
    });
  };
  const [accepted, setAccepted] = useState({});
  const [comments, setComments] = useState("");
  const [signature, setSignature] = useState("");
  const [signatureValid, setSignatureValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Pré-sélection d'un cours depuis le planning (?course=slug ou ?course=<id>).
  // On l'ajoute automatiquement au premier enfant.
  React.useEffect(() => {
    if (!preselectedCourse || COURSE_OPTIONS.length === 0) return;
    const found = COURSE_OPTIONS.find(
      (c) => c.id === preselectedCourse || c.slug === preselectedCourse
    );
    if (!found) return;
    // Forfait : la présélection depuis le planning amène AUSSI le second entraînement.
    const toAdd = forfaitSessions(found, COURSE_OPTIONS);
    setChildren((arr) =>
      arr.map((c, i) => {
        if (i !== 0) return c;
        const missing = toAdd.filter((co) => !c.courses.some((x) => x.id === co.id));
        return missing.length ? { ...c, courses: [...c.courses, ...missing] } : c;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedCourse, COURSE_OPTIONS]);

  const pricing = useMemo(
    () => computeFamilyPricing(children, parent.first_registration, isAdultSelf),
    [children, parent.first_registration, isAdultSelf]
  );
  // Barème appliqué à cet élève : case « adulte » du formulaire, sinon âge au 24 août.
  const childIsAdult = (c) => isAdultForTarifs(c, isAdultSelf);
  const lignesEleve = (idx) => pricing.childSubtotals[idx]?.lines || [];

  // Un enfant déjà connu de la famille connectée (même prénom et nom, accents et casse
  // ignorés) n'est pas un « nouvel élève » : la taxe administrative ne lui est pas annoncée.
  const cleNom = (p, n) => `${p || ""} ${n || ""}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z]+/g, " ").trim();
  const estEnfantConnu = (c) => {
    const cle = cleNom(c.first_name, c.last_name);
    return !!cle && (famille?.enfants || []).some((e) => cleNom(e.prenom, e.nom) === cle);
  };
  const updateChild = (idx, k, v) => {
    setChildren((arr) => arr.map((c, i) => {
      if (i !== idx) return c;
      const maj = { ...c, [k]: v };
      if ((k === "first_name" || k === "last_name") && estEnfantConnu(maj)) maj.nouvel_eleve = false;
      return maj;
    }));
  };
  const addChild = () => setChildren((arr) => [...arr, blankChild()]);
  const removeChild = (idx) => setChildren((arr) => arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr);
  const addCourseToChild = (idx, courseId) => {
    const course = COURSE_OPTIONS.find((c) => c.id === courseId);
    if (!course) return;
    // Forfait Compétition/Compagnie : les 2 entraînements hebdomadaires sont
    // indissociables — cocher l'un inscrit automatiquement à l'autre.
    const toAdd = forfaitSessions(course, COURSE_OPTIONS);
    setChildren((arr) => arr.map((c, i) => {
      if (i !== idx) return c;
      const missing = toAdd.filter((co) => !c.courses.some((x) => x.id === co.id));
      return missing.length ? { ...c, courses: [...c.courses, ...missing] } : c;
    }));
  };
  const removeCourseFromChild = (childIdx, courseId) => {
    const course = COURSE_OPTIONS.find((c) => c.id === courseId);
    const toRemove = new Set(
      (course ? forfaitSessions(course, COURSE_OPTIONS) : []).map((c) => c.id)
    );
    toRemove.add(courseId);
    setChildren((arr) => arr.map((c, i) =>
      i === childIdx ? { ...c, courses: c.courses.filter((co) => !toRemove.has(co.id)) } : c
    ));
  };

  // Un cours de compétition ou de compagnie déclenche un règlement spécifique
  // à retourner signé par l'élève ET son représentant légal avant toute participation.
  const enCompetition = (c) => (c.courses || []).some((co) => co && co.discipline === "Compétition");
  const hasCompetition = children.some(enCompetition);
  // Droit à l'image PAR ÉLÈVE (demande de l'école, 09.10.2026) : il n'y avait qu'une réponse
  // pour toute la famille, et un enfant en compétition faisait passer ses frères et sœurs à
  // « Oui » d'office. L'accord reste obligatoire pour l'élève inscrit en Compétition ou en
  // Compagnie (photographié et filmé aux concours), et pour lui seul.
  const photoDe = (c) => (enCompetition(c) ? "oui" : c.photo_consent === "non" ? "non" : "oui");
  const photos = children.map(photoDe);
  // Réponse de la famille gardée pour les anciens lecteurs : « mixte » quand les enfants
  // n'ont pas répondu pareil (le détail est sur chaque enfant).
  const photoFamille = photos.every((v) => v === "oui") ? "oui"
    : photos.every((v) => v === "non") ? "non" : "mixte";

  const TERMS = [
    { k: "reglement", t: "J'ai lu et j'accepte le règlement général de 1000ARTS." },
    ...(hasCompetition
      ? [{
          k: "reglement_competition",
          t: "J'ai lu le règlement Compétition & Compagnie et je m'engage à le retourner signé par l'élève et son représentant légal avant la première participation.",
        }]
      : []),
  ];

  // La date de naissance est demandée à tout le monde, adultes compris : elle fixe le
  // barème, et les attestations d'assurance la réclament.
  const childrenValid = children.every((c) =>
    c.first_name && c.last_name && c.birth_date && !birthDateError(c.birth_date) && !erreurAvs(c.avs)
  );
  // En mode adulte, parent.name est construit depuis first_name+last_name (donc name = suffit)
  // En mode parent, parent.first_name+last_name doivent tous deux être remplis.
  const parentValid = parent.first_name && parent.last_name && isValidEmail(parent.email)
    && parent.phone && parent.address && parent.npa && parent.city && parent.source_connaissance;
  const coursesValid = children.every((c) => c.courses.length > 0 || c.pass_id);
  // Ce qui manque à l'étape 1 : « Suivant » restait grisé sans dire pourquoi (oublier
  // « Comment as-tu connu 1000 ARTS ? » bloquait sans un mot — recette du 08.10.2026).
  const manquantsEtape1 = [];
  children.forEach((c, i) => {
    const qui = isAdultSelf ? "" : children.length > 1 ? ` (enfant ${i + 1})` : " de l'enfant";
    if (!c.first_name || !c.last_name) manquantsEtape1.push(isAdultSelf ? "ton prénom et ton nom" : `le prénom et le nom${qui}`);
    if (!c.birth_date) manquantsEtape1.push(`la date de naissance${qui}`);
    else if (birthDateError(c.birth_date)) manquantsEtape1.push(`une date de naissance valide${qui}`);
    if (erreurAvs(c.avs)) manquantsEtape1.push(`un numéro AVS valide${qui} (ou le laisser vide)`);
  });
  if (!parent.first_name || !parent.last_name) {
    manquantsEtape1.push(isAdultSelf ? "ton prénom et ton nom (coordonnées)" : "le prénom et le nom du représentant légal");
  }
  if (!isValidEmail(parent.email)) manquantsEtape1.push("une adresse e-mail valide");
  if (!parent.phone) manquantsEtape1.push("un numéro de téléphone");
  if (!parent.address || !parent.npa || !parent.city) manquantsEtape1.push("l'adresse complète (rue, NPA, localité)");
  if (!parent.source_connaissance) manquantsEtape1.push("la réponse à « Comment as-tu connu 1000 ARTS ? »");
  // En compétition, l'accord à l'image fait partie de l'inscription (les élèves sont
  // photographiés et filmés lors des concours) : photoDe le donne d'office à l'élève
  // concerné, ses frères et sœurs gardent leur choix.
  const termsValid = TERMS.every((t) => accepted[t.k]) && signatureValid && signature.trim().length > 1;

  const sending = useRef(false);
  const submit = async () => {
    if (sending.current) return;
    sending.current = true;
    setLoading(true); setError("");
    try {
      // Le montant écrit dans le tableur vient du calcul affiché à l'écran : on ne
      // renvoie jamais un prix stocké sur l'objet cours (il pourrait dater d'avant un
      // changement de date de naissance ou de la case « adulte »).
      const childrenPayload = children.map((child, i) => {
        const prix = new Map();
        (pricing.childSubtotals[i]?.lines || []).forEach((g) => {
          g.courses.forEach((co) => prix.set(co.id, g.price));
        });
        return { ...child, photo_consent: photoDe(child),
          courses: child.courses.map((co) => ({ ...co, price: prix.get(co.id) ?? null })) };
      });
      await api.post("/inscriptions/semestrielle", {
        parent: { ...parent, first_registration: isAdultSelf ? parent.first_registration : children.some((c) => c.nouvel_eleve) },
        children: childrenPayload, accepted, photo_consent: photoFamille, comments, signature,
        is_adult_self: isAdultSelf,
        has_competition: hasCompetition,
        pricing,
      });
      setSuccess(true);
      setStep(5);
    } catch (e) {
      setError(messageErreurEnvoi(e));
    } finally { setLoading(false); sending.current = false; }
  };

  if (success) {
    return (
      <div data-testid="semestrielle-success" className="text-center py-12">
        <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "var(--primary)" }}>
          <Check size={28} color="#fff" />
        </div>
        <h2 className="font-display text-3xl md:text-4xl">Merci, ton inscription a bien été envoyée.</h2>
        <p className="text-[var(--muted)] mt-4 max-w-lg mx-auto">Tu recevras une confirmation tout bientôt !</p>
      </div>
    );
  }

  return (
    <div data-testid="semestrielle-form" className="space-y-8">
      <button onClick={onBack} data-testid="semestrielle-back" className="btn-ghost"><ArrowLeft size={14} /> Retour</button>

      <div>
        <div className="eyebrow mb-2">Cours semestriel</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Inscription au semestre</h2>
      </div>

      {/* STEPPER */}
      <div className="stepper" data-testid="stepper">
        {[isAdultSelf ? "Vous" : "Enfant(s)", "Cours", "Récapitulatif", "Validation"].map((label, i) => {
          const n = i + 1;
          const cls = step === n ? "active" : step > n ? "done" : "";
          return (
            <React.Fragment key={n}>
              <div className={`step ${cls}`}>
                <span className="step-num">{step > n ? <Check size={14} /> : n}</span>
                <span className="hidden md:inline">{label}</span>
              </div>
              {n < 4 && <div className={`divider ${step > n ? "done" : ""}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* STEP 1: ENFANTS + PARENT */}
      {step === 1 && (
        <div data-testid="step-1" className="space-y-6">
          <BandeauConnecte moi={famille}
            texte={famille?.enfants?.length ? "Tes coordonnées sont remplies : choisis simplement l'élève plus bas."
              : "Tes coordonnées sont déjà remplies."} />
          {/* Toggle Adulte pour lui-même */}
          <section className="card-flat" style={{ borderColor: "var(--primary)", background: "var(--primary-tint)" }} data-testid="sem-adulte-toggle">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="sem-adult-self"
                className="mt-1 accent-[var(--primary)] w-5 h-5"
                checked={isAdultSelf}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsAdultSelf(checked);
                  if (checked) {
                    if (children.length > 1) setChildren(children.slice(0, 1));
                    // Copie les infos parent déjà remplies vers children[0] pour éviter que
                    // le bouton "Suivant" reste bloqué à cause d'un children[0] vide.
                    setChildren((arr) => arr.map((c, i) => i === 0 ? {
                      ...c,
                      first_name: c.first_name || parent.first_name,
                      last_name: c.last_name || parent.last_name,
                    } : c));
                  }
                }}
              />
              <div>
                <div className="font-display text-lg">Je m'inscris pour moi-même (adulte)</div>
                <div className="text-sm text-[var(--muted)] mt-1">Coche si tu es un adulte qui s'inscrit pour toi (pas pour ton enfant).</div>
              </div>
            </label>
          </section>

          <section className="card-flat">
            <div className="font-display text-xl mb-5">{isAdultSelf ? "Tes coordonnées" : "Représentant légal (une fois pour la famille)"}</div>
            <div className="grid md:grid-cols-2 gap-4">
              {isAdultSelf ? (
                <>
                  <div><label className="field-label">Prénom *</label><input data-testid="sem-adult-first" className="input-field" value={parent.first_name} onChange={(e) => {
                    const v = e.target.value;
                    setParent({ ...parent, first_name: v, name: `${v} ${parent.last_name}`.trim() });
                    setChildren((arr) => arr.map((c, i) => i === 0 ? { ...c, first_name: v } : c));
                  }} /></div>
                  <div><label className="field-label">Nom *</label><input data-testid="sem-adult-last" className="input-field" value={parent.last_name} onChange={(e) => {
                    const v = e.target.value;
                    setParent({ ...parent, last_name: v, name: `${parent.first_name} ${v}`.trim() });
                    setChildren((arr) => arr.map((c, i) => i === 0 ? { ...c, last_name: v } : c));
                  }} /></div>
                  {/* Un adulte qui s'inscrit pour lui-même ne passe pas par le bloc
                      « Enfant 1 » : sans ces champs, sa date de naissance et son numéro
                      AVS n'étaient jamais demandés. */}
                  <div className="md:col-span-2">
                    <label className="field-label">Date de naissance *</label>
                    <ChampNaissance
                      testid="sem-adult-birth"
                      value={children[0]?.birth_date || ""}
                      onChange={(v) => updateChild(0, "birth_date", v)}
                    />
                    {birthDateError(children[0]?.birth_date) && children[0]?.birth_date ? (
                      <p data-testid="sem-adult-birth-error" className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }}>
                        {birthDateError(children[0]?.birth_date)}
                      </p>
                    ) : (
                      <p className="text-xs mt-1 text-[var(--muted)]">
                        Demandée pour les attestations d'assurance maladie.
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label">N° AVS (facultatif)</label>
                    <input
                      data-testid="sem-adult-avs"
                      className="input-field"
                      inputMode="numeric"
                      autoComplete="off"
                      value={children[0]?.avs || ""}
                      onChange={(e) => updateChild(0, "avs", formatAvs(e.target.value))}
                      placeholder={AVS_PLACEHOLDER}
                    />
                    {erreurAvs(children[0]?.avs) ? (
                      <p data-testid="sem-adult-avs-error" className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }}>
                        {erreurAvs(children[0]?.avs)}
                      </p>
                    ) : children[0]?.avs ? (
                      <p data-testid="sem-adult-avs-ok" className="text-xs mt-1 font-medium" style={{ color: "#15803D" }}>
                        Numéro valide ✓
                      </p>
                    ) : (
                      <p className="text-xs mt-1 text-[var(--muted)]">
                        Pour bénéficier d'un remboursement par votre assurance complémentaire, indiquez votre numéro AVS à 13 chiffres commençant par 756 (disponible sur votre carte d'assurance-maladie, certificat de salaire ou déclaration d'impôt).
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div><label className="field-label">Prénom *</label><input data-testid="sem-parent-first-name" className="input-field" value={parent.first_name} onChange={(e) => {
                    const v = e.target.value;
                    setParent({ ...parent, first_name: v, name: `${v} ${parent.last_name}`.trim() });
                  }} /></div>
                  <div><label className="field-label">Nom *</label><input data-testid="sem-parent-last-name" className="input-field" value={parent.last_name} onChange={(e) => {
                    const v = e.target.value;
                    setParent({ ...parent, last_name: v, name: `${parent.first_name} ${v}`.trim() });
                  }} /></div>
                </>
              )}
              <div><label className="field-label">E-mail *</label><input data-testid="sem-parent-email" type="email" className="input-field" value={parent.email} onChange={(e) => setParent({ ...parent, email: e.target.value })} /><EmailSuggestion value={parent.email} onFix={(v) => setParent({ ...parent, email: v })} testid="sem-email-suggestion" /></div>
              <div><label className="field-label">Téléphone *</label><input data-testid="sem-parent-phone" type="tel" inputMode="tel" placeholder="+41 79 950 40 31" className="input-field" value={parent.phone} onChange={(e) => setParent({ ...parent, phone: formatSwissPhone(e.target.value) })} /></div>
              <div className="md:col-span-2"><label className="field-label">Adresse *</label><input data-testid="sem-parent-address" className="input-field" placeholder="Rue et numéro" value={parent.address} onChange={(e) => setParent({ ...parent, address: e.target.value })} /></div>
              <div>
                <label className="field-label">NPA *</label>
                <input
                  data-testid="sem-parent-npa"
                  className="input-field"
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  placeholder="1053"
                  value={parent.npa}
                  onChange={(e) => {
                    const npa = e.target.value.replace(/\D/g, "").slice(0, 4);
                    const suggested = cityFromNpa(npa);
                    setParent((p) => ({
                      ...p,
                      npa,
                      // Auto-remplit la localité si elle est vide ou si elle vient d'une précédente auto-complétion.
                      city: suggested && isAutoFilledCity(p.city) ? suggested : p.city,
                    }));
                  }}
                />
              </div>
              <div>
                <label className="field-label">Localité *</label>
                <input data-testid="sem-parent-city" className="input-field" placeholder="Cugy VD" value={parent.city} onChange={(e) => setParent({ ...parent, city: e.target.value })} />
              </div>
              <div className="flex items-end">
                {/* La taxe administrative est due par ÉLÈVE, pas par famille : la case est
                    donc sur chaque enfant. Ici, seul le cas « un adulte s'inscrit pour
                    lui-même » utilise encore une case au niveau du compte. */}
                {isAdultSelf && (
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={parent.first_registration} onChange={(e) => setParent({ ...parent, first_registration: e.target.checked })} className="accent-[var(--primary)]" data-testid="sem-first-registration" />
                    Première inscription dans l'école (+ CHF {PARAMETRES.taxe_admin_premiere_inscription} taxe admin)
                  </label>
                )}
              </div>
            </div>
          </section>

          {/* Comment la famille a connu l'école : l'école pilote sa communication avec ça. */}
          <section className="card-flat" data-testid="sem-source-box">
            <label className="field-label">Comment as-tu connu 1000 ARTS ? *</label>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {SOURCES_CONNAISSANCE.map((s) => (
                <label key={s} data-testid={`sem-source-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-2 cursor-pointer text-sm rounded-xl px-3 py-2"
                  style={{ border: `1px solid ${parent.source_connaissance === s ? "var(--primary)" : "var(--border)"}`,
                    background: parent.source_connaissance === s ? "var(--primary-tint)" : "#fff" }}>
                  <input type="radio" name="source" className="accent-[var(--primary)]"
                    checked={parent.source_connaissance === s}
                    onChange={() => setParent({ ...parent, source_connaissance: s })} />
                  {s}
                </label>
              ))}
            </div>
            {parent.source_connaissance === "Autre" && (
              <input data-testid="sem-source-precision" className="input-field mt-3"
                placeholder="Dis-nous comment (affiche, passage devant l'école, école, club…)"
                value={parent.source_precision || ""}
                onChange={(e) => setParent({ ...parent, source_precision: e.target.value })} />
            )}
          </section>

          {/* Parrainage — champ optionnel Semestriel */}
          <section className="card-flat" style={{ background: "var(--primary-tint)", borderColor: "transparent" }} data-testid="sem-parrainage-box">
            <label className="field-label flex items-center gap-2">
              🎁 Tu as été parrainé·e ? <span className="text-[var(--muted)] font-normal">(optionnel)</span>
            </label>
            <p className="text-xs text-[var(--muted)] mb-3 leading-snug">
              Indique le prénom et le nom de la personne qui t'a recommandé 1000 ARTS. Elle recevra <strong>CHF 50.–</strong> de réduction déduite directement sur sa prochaine facture semestrielle.
            </p>
            <input
              data-testid="sem-parrain"
              className="input-field"
              placeholder="Ex. Julie Martin"
              value={parent.parrain || ""}
              onChange={(e) => setParent({ ...parent, parrain: e.target.value })}
            />
          </section>

          {!isAdultSelf && (
            <ElevesConnus moi={famille} choisis={elevesChoisis} onChoisir={choisirEleveConnu} />
          )}

          {/* Section Enfant : cachée en mode adulte */}
          {!isAdultSelf && children.map((c, idx) => (
            <section key={idx} className="card-flat" data-testid={`enfant-${idx}`}>
              <div className="flex items-center justify-between mb-5">
                <div className="font-display text-xl">Enfant {idx + 1}</div>
                {children.length > 1 && (
                  <button data-testid={`remove-enfant-${idx}`} onClick={() => removeChild(idx)} className="text-sm text-[var(--muted)] inline-flex items-center gap-1 hover:text-red-600"><Trash2 size={14} /> Retirer</button>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="field-label">Prénom *</label><input data-testid={`enfant-${idx}-first`} className="input-field" value={c.first_name} onChange={(e) => updateChild(idx, "first_name", e.target.value)} /></div>
                <div><label className="field-label">Nom *</label><input data-testid={`enfant-${idx}-last`} className="input-field" value={c.last_name} onChange={(e) => updateChild(idx, "last_name", e.target.value)} /></div>
                <div>
                  <label className="field-label">Date de naissance *</label>
                  <ChampNaissance testid={`enfant-${idx}-birth`} value={c.birth_date} onChange={(v) => updateChild(idx, "birth_date", v)} />
                  {birthDateError(c.birth_date) ? (
                    <p data-testid={`enfant-${idx}-birth-error`} className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }}>
                      {birthDateError(c.birth_date)}
                    </p>
                  ) : c.birth_date ? (
                    <p data-testid={`enfant-${idx}-birth-age`} className="text-xs mt-1 text-[var(--muted)]">
                      {ageAt(c.birth_date)} ans à la rentrée · tarif {childIsAdult(c) ? "adultes" : "enfants & ados"}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-end">
                  {/* La taxe administrative est due par élève, une seule fois dans sa vie. */}
                  <label className="flex items-start gap-2 cursor-pointer text-sm leading-snug">
                    <input
                      type="checkbox"
                      checked={!!c.nouvel_eleve}
                      onChange={(e) => updateChild(idx, "nouvel_eleve", e.target.checked)}
                      className="accent-[var(--primary)] mt-0.5"
                      data-testid={`enfant-${idx}-nouvel-eleve`}
                    />
                    <span>
                      Nouvel élève — jamais inscrit à 1000 ARTS
                      <span className="text-[var(--muted)]"> (+ CHF {PARAMETRES.taxe_admin_premiere_inscription} de taxe administrative, une seule fois)</span>
                    </span>
                  </label>
                </div>
                <div>
                  <label className="field-label">N° AVS (facultatif)</label>
                  <input
                    data-testid={`enfant-${idx}-avs`}
                    className="input-field"
                    inputMode="numeric"
                    autoComplete="off"
                    value={c.avs || ""}
                    onChange={(e) => updateChild(idx, "avs", formatAvs(e.target.value))}
                    placeholder={AVS_PLACEHOLDER}
                  />
                  {erreurAvs(c.avs) ? (
                    <p data-testid={`enfant-${idx}-avs-error`} className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }}>
                      {erreurAvs(c.avs)}
                    </p>
                  ) : c.avs ? (
                    <p data-testid={`enfant-${idx}-avs-ok`} className="text-xs mt-1 font-medium" style={{ color: "#15803D" }}>
                      Numéro valide ✓
                    </p>
                  ) : (
                    <p className="text-xs mt-1 text-[var(--muted)]">
                      Pour bénéficier d'un remboursement par votre assurance complémentaire, indiquez votre numéro AVS à 13 chiffres commençant par 756 (disponible sur votre carte d'assurance-maladie, certificat de salaire ou déclaration d'impôt).
                    </p>
                  )}
                </div>
                <div><label className="field-label">Commentaire / infos particulières</label><input data-testid={`enfant-${idx}-comments`} className="input-field" value={c.comments} onChange={(e) => updateChild(idx, "comments", e.target.value)} placeholder="Allergies, niveau précédent…" /></div>
              </div>
            </section>
          ))}

          {!isAdultSelf && (
            <button onClick={addChild} data-testid="add-child" className="btn-outline w-full justify-center">
              <Plus size={16} /> Ajouter un enfant
            </button>
          )}

          {(!parentValid || !childrenValid) && manquantsEtape1.length > 0 && (
            <div className="p-4 rounded-2xl text-sm" data-testid="sem-manquants-etape-1"
              style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}>
              <div className="font-semibold mb-2">Pour continuer, il te reste à remplir&nbsp;:</div>
              <ul className="space-y-1 list-disc pl-5">
                {[...new Set(manquantsEtape1)].map((m) => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={() => setStep(2)} disabled={!parentValid || !childrenValid} data-testid="step-1-next" className="btn-primary">
              Suivant <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: COURSES */}
      {step === 2 && (
        <div data-testid="step-2" className="space-y-6">
          {children.map((c, idx) => {
            const hasCompCourse = (c.courses || []).some((course) => {
              if (course && typeof course === "object" && "discipline" in course) return course.discipline === "Compétition";
              const opt = COURSE_OPTIONS.find((o) => o.id === course);
              return opt && opt.discipline === "Compétition";
            });
            const selectedPass = c.pass_id ? PASSES.find((p) => p.id === c.pass_id) : null;
            // Quand la personne s'inscrit pour elle-même, seuls les pass adultes ont du sens.
            const eligiblePasses = isAdultSelf
              ? PASSES.filter((p) => p.audience === "adulte")
              : PASSES.filter((p) => isPassEligibleForChild(p, c));
            // Suggestion : le pass le moins cher qui devient réellement avantageux, comparé au
            // montant VRAIMENT payé (réductions d'accumulation déduites).
            const better = selectedPass ? null : findBetterPass(c.courses, eligiblePasses, childIsAdult(c));
            return (
            <section key={idx} className="card-flat">
              <div className="font-display text-xl mb-4">Cours pour {c.first_name || `Enfant ${idx + 1}`}</div>

              {/* SEGMENTED CONTROL — Cours individuels vs Pass illimité */}
              <div
                className="inline-flex p-1 rounded-full mb-5"
                style={{ background: "var(--bg-soft)" }}
                data-testid={`formula-toggle-${idx}`}
                role="tablist"
                aria-label={`Formule d'inscription pour l'enfant ${idx + 1}`}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!selectedPass}
                  onClick={() => updateChild(idx, "pass_id", "")}
                  data-testid={`formula-courses-${idx}`}
                  className="px-4 py-1.5 rounded-full text-sm font-medium transition-colors"
                  style={!selectedPass
                    ? { background: "var(--primary)", color: "#fff" }
                    : { background: "transparent", color: "var(--muted)" }}
                >
                  Cours individuels
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!!selectedPass}
                  onClick={() => {
                    if (!selectedPass && eligiblePasses.length > 0) {
                      updateChild(idx, "pass_id", eligiblePasses[0].id);
                    }
                  }}
                  data-testid={`formula-pass-${idx}`}
                  className="px-4 py-1.5 rounded-full text-sm font-medium transition-colors"
                  style={selectedPass
                    ? { background: "var(--primary)", color: "#fff" }
                    : { background: "transparent", color: "var(--muted)" }}
                >
                  ✨ Pass illimité
                </button>
              </div>

              {/* Suggestion Pass — le pass le moins cher qui fait réellement économiser */}
              {better && (
                <div
                  data-testid={`pass-suggestion-${idx}`}
                  className="mb-5 rounded-2xl p-4 flex items-start gap-3"
                  style={{ background: "var(--primary-tint)", borderLeft: "4px solid var(--primary)" }}
                >
                  <span className="text-2xl leading-none">💡</span>
                  <div className="text-sm leading-relaxed flex-1">
                    <strong>Le {better.pass.label} (CHF {better.pass.price}.–) serait plus avantageux</strong> — tes {(c.courses || []).length} cours reviennent à CHF {better.realCost}.– réductions déduites, tu économiserais donc <strong>CHF {better.savings}.–</strong>. {better.pass.tagline}.
                    <button
                      type="button"
                      onClick={() => updateChild(idx, "pass_id", better.pass.id)}
                      data-testid={`pass-suggestion-apply-${idx}`}
                      className="ml-2 underline font-medium hover:opacity-80"
                      style={{ color: "var(--primary)" }}
                    >
                      Choisir ce pass →
                    </button>
                  </div>
                </div>
              )}

              {/* SÉLECTION DU PASS */}
              {selectedPass && (
                <div
                  data-testid={`pass-selector-${idx}`}
                  className="rounded-2xl p-5 mb-5"
                  style={{ background: "var(--primary-tint)", border: "1.5px solid var(--primary)" }}
                >
                  <div className="font-display text-lg mb-3 flex items-center gap-2">
                    <Sparkles size={18} style={{ color: "var(--primary)" }} />
                    Choix du pass illimité
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    {eligiblePasses.map((p) => {
                      const active = c.pass_id === p.id;
                      return (
                        <label
                          key={p.id}
                          className="cursor-pointer rounded-2xl p-4 flex flex-col gap-2 bg-white"
                          style={{ border: `1.5px solid ${active ? "var(--primary)" : "var(--border-soft)"}` }}
                          data-testid={`pass-option-${idx}-${p.id}`}
                        >
                          <div className="flex items-start gap-2">
                            <input
                              type="radio"
                              name={`pass-${idx}`}
                              checked={active}
                              onChange={() => updateChild(idx, "pass_id", p.id)}
                              className="mt-1 accent-[var(--primary)]"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{p.label}</div>
                              <div className="text-xs text-[var(--muted)] mt-0.5">{p.tagline}</div>
                            </div>
                          </div>
                          <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>CHF {p.price}.–</div>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[var(--muted)] mt-3">
                    💡 Un pass illimité remplace la somme des cours individuels et neutralise la réduction accumulation. <strong>Important :</strong> chaque cours ajouté ci-dessous réserve une place pour l'élève — n'ajoute donc que les cours qu'il ou elle suivra réellement.
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <select
                  data-testid={`select-course-${idx}`}
                  className="input-field"
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) { addCourseToChild(idx, e.target.value); e.target.value = ""; } }}
                >
                  <option value="">
                    {planningLoading
                      ? "Chargement du planning…"
                      : selectedPass ? "+ Ajouter un cours au planning (réservé pour l'élève)" : "+ Ajouter un cours pour cet élève"}
                  </option>
                  {COURSE_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {courseOptionLabel(opt, childIsAdult(c))}{inscriptionPourLaRentree(opt) ? ` — ${LABEL_PLACES_RENTREE}` : seatState(opt).waitlist ? " — COMPLET (liste d'attente)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Encart dynamique — apparaît si un cours Compétition/Compagnie est sélectionné */}
              {hasCompCourse && (
                <div
                  data-testid={`competition-audition-callout-${idx}`}
                  className="mt-4 rounded-2xl p-5"
                  style={{ background: "#FFF7ED", borderLeft: "4px solid #F97316", color: "#7C2D12" }}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <span className="text-2xl leading-none shrink-0">⚠️</span>
                    <div className="text-sm leading-relaxed flex-1">
                      <strong className="block mb-1">Cours sur audition uniquement</strong>
                      Les cours Compétition et Compagnie ne sont accessibles qu'aux danseurs sélectionnés lors de l'audition. Si {c.first_name || "ton enfant"} n'a pas encore passé d'audition, inscris-le·la d'abord via le <Link to="/inscription/audition" className="underline font-medium">formulaire d'audition</Link>.
                    </div>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 accent-[var(--primary)]"
                      data-testid={`audition-confirm-${idx}`}
                      checked={!!c.audition_confirmed}
                      onChange={(e) => updateChild(idx, "audition_confirmed", e.target.checked)}
                    />
                    <span className="text-sm text-[#7C2D12]">
                      <strong>Je confirme que {c.first_name || "l'élève"} a passé l'audition et a été sélectionné·e</strong> par 1000 Arts.
                    </span>
                  </label>
                </div>
              )}

              {c.courses.length > 0 && (
                <div className="mt-4 space-y-2">
                  {lignesEleve(idx).map((g) => {
                    const isForfait = g.type === "forfait";
                    const head = g.courses[0];
                    // Forfait : si UN des deux entraînements est complet, tout le forfait
                    // part en liste d'attente — jamais d'inscription à moitié.
                    const waitlist = g.courses.some((co) => seatState(co).waitlist);
                    const urgent = g.courses.some((co) => seatState(co).urgent);
                    // Après le 1er février, sur un cours qui n'accepte plus d'élève, l'état
                    // des places de la saison en cours n'a plus d'importance : la famille
                    // réserve la rentrée. Ce message l'emporte donc sur « Complet ».
                    const pourLaRentree = g.courses.some((co) => inscriptionPourLaRentree(co));
                    const testKey = isForfait
                      ? `forfait-pill-${idx}-${g.forfait.toLowerCase().replace(/\s+/g, "-")}`
                      : `course-pill-${idx}-${head.id}`;
                    return (
                    <div key={g.key} data-testid={testKey} className="p-4 rounded-2xl" style={{ background: "var(--primary-tint)" }}>
                     <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{isForfait ? g.label : head.name}</div>
                        {isForfait && (
                          <div className="text-xs font-medium mt-0.5" style={{ color: "var(--primary)" }}>
                            {FORFAIT_SUBLABEL}
                          </div>
                        )}
                        {g.courses.map((co) => (
                          <div key={co.id} className="text-sm text-[var(--muted)]">
                            {isForfait ? `${co.name} · ` : ""}{co.day} · {co.time} · {co.room}
                          </div>
                        ))}
                        {pourLaRentree ? (
                          <div className="text-xs mt-1 font-medium" style={{ color: "#1D4ED8" }} data-testid={`course-late-join-${idx}-${head.id}`}>
                            {MESSAGE_INSCRIPTION_TARDIVE}
                          </div>
                        ) : waitlist ? (
                          <div className="text-xs font-semibold mt-1" style={{ color: "#B45309" }} data-testid={`course-waitlist-${idx}-${head.id}`}>
                            {isForfait
                              ? "Un des deux entraînements est complet — le forfait entier sera enregistré en liste d'attente."
                              : "Complet — cette demande sera enregistrée en liste d'attente."}
                          </div>
                        ) : head.message_site ? (
                          <div className="text-xs mt-1" style={{ color: urgent ? "#B45309" : "var(--muted)" }}>{head.message_site}</div>
                        ) : null}
                        {/* Avertissement NON bloquant : l'école accepte les dérogations d'âge
                            en connaissance de cause, mais la famille doit être prévenue. */}
                        {g.courses.map((co) => ageWarningForCourse(c, co)).find(Boolean) && (
                          <div className="text-xs mt-1 font-medium" style={{ color: "#B45309" }} data-testid={`course-age-warning-${idx}-${head.id}`}>
                            {g.courses.map((co) => ageWarningForCourse(c, co)).find(Boolean)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-display text-lg whitespace-nowrap" style={{ opacity: selectedPass ? 0.4 : 1, textDecoration: selectedPass ? "line-through" : "none" }}>
                          CHF {g.price}.–
                        </span>
                        <button onClick={() => removeCourseFromChild(idx, head.id)} data-testid={`remove-course-${idx}-${head.id}`} className="text-[var(--muted)] hover:text-red-600"><X size={18} /></button>
                      </div>
                     </div>
                        {/* La carte 10 cours a son propre formulaire depuis le 08.10.2026
                            (décision de l'école) : ici, le cours se prend au semestre. Sous un
                            cours concerné, un lien mène au formulaire de la carte, le cours
                            déjà coché. */}
                        {!isForfait && !selectedPass && carte10Eligible(head) && (
                          <div className="mt-2 text-xs text-[var(--muted)]" data-testid={`carte10-lien-${idx}-${head.id}`}>
                            Tu préfères venir à la carte ?{" "}
                            <Link to={`/inscription/carte-10?course=${encodeURIComponent(head.id)}`}
                              className="font-medium underline" style={{ color: "var(--primary)" }}>
                              → formulaire Carte {CARTE_10.nb_seances} cours
                            </Link>
                          </div>
                        )}
                    </div>
                    );
                  })}
                </div>
              )}
              {c.courses.length === 0 && !selectedPass && <p className="text-sm text-[var(--muted)] mt-3">Aucun cours sélectionné pour le moment.</p>}
            </section>
            );
          })}

          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-ghost"><ArrowLeft size={14} /> Précédent</button>
            <button onClick={() => setStep(3)} disabled={!coursesValid} data-testid="step-2-next" className="btn-primary">Suivant <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 3: RÉCAPITULATIF */}
      {step === 3 && (
        <div data-testid="step-3" className="space-y-6">
          <section className="card-flat">
            <div className="font-display text-xl mb-5">Récapitulatif</div>
            {pricing.childSubtotals.map((ch, i) => (
              <div key={i} className="mb-5 pb-5 border-b border-[var(--border-soft)] last:border-0 last:mb-0 last:pb-0">
                <div className="font-medium mb-3">{ch.name}</div>
                {ch.pass ? (
                  <>
                    <div className="flex justify-between items-center text-sm py-2">
                      <span className="text-[var(--muted)] flex items-center gap-2">
                        <Sparkles size={14} style={{ color: "var(--primary)" }} />
                        {ch.pass.label} <span className="text-xs italic">— {ch.pass.tagline}</span>
                      </span>
                      <span>CHF {ch.pass.price}.–</span>
                    </div>
                    {ch.courses.length > 0 && (
                      <div className="text-xs text-[var(--muted)] italic mt-1 pl-5">
                        Cours réservés : {ch.courses.map((co) => co.name).join(", ")}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {(ch.lines || []).map((g) => (
                      <div key={g.key} className="flex justify-between items-start text-sm py-2">
                        <span className="text-[var(--muted)]">
                          {g.type === "forfait" ? (
                            <>
                              <span className="font-medium text-[var(--text)]">{g.label}</span>
                              <span className="block text-xs italic">
                                {g.courses.map((co) => `${co.day} ${co.time}`).join(" + ")} — {FORFAIT_SUBLABEL}
                              </span>
                            </>
                          ) : (
                            <>
                              {`${g.courses[0].day} ${g.courses[0].time} — ${g.label}`}
                            </>
                          )}
                        </span>
                        <span className="whitespace-nowrap">CHF {g.price}.–</span>
                      </div>
                    ))}
                    {ch.accumDisc > 0 && (
                      <div className="flex justify-between items-center text-sm py-2" style={{ color: "var(--primary)" }}>
                        <span>
                          Réduction accumulation ({ch.accumCount} cours)
                          <span className="block text-xs opacity-80" data-testid="recap-accumulation-detail">
                            {detailAccumulation(ch.accumCount)}
                          </span>
                        </span>
                        <span className="whitespace-nowrap">– CHF {ch.accumDisc}.–</span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between items-center pt-3 font-medium">
                  <span>Sous-total {ch.name}</span>
                  <span>CHF {ch.subtotal - ch.accumDisc}.–</span>
                </div>
              </div>
            ))}

            <div className="mt-6 space-y-2 text-sm">
              <div className="flex justify-between"><span>Total des cours</span><span>CHF {pricing.total}.–</span></div>
              {pricing.accumulation > 0 && <div className="flex justify-between" style={{ color: "var(--primary)" }}><span>Réductions accumulation</span><span>– CHF {pricing.accumulation}.–</span></div>}
              {pricing.discount > 0 && <div className="flex justify-between" style={{ color: "var(--primary)" }}><span>Réduction famille ({pricing.enfantsEligibles} enfants)</span><span>– CHF {pricing.discount}.–</span></div>}
              {pricing.adminFee > 0 && <div className="flex justify-between"><span>Taxe administrative{pricing.adminFeeCount > 1 ? ` (${pricing.adminFeeCount} nouveaux élèves × CHF ${pricing.adminFeeUnit}.–)` : " (nouvel élève)"}</span><span>+ CHF {pricing.adminFee}.–</span></div>}
            </div>

            <div className="mt-6 pt-6 border-t border-[var(--border-soft)] flex justify-between items-baseline">
              <span className="font-display text-xl">Total estimé pour le semestre</span>
              <span className="font-display text-3xl" style={{ color: "var(--primary)" }}>CHF {pricing.final}.–</span>
            </div>
            <div
              data-testid="prorata-notice"
              className="mt-5 rounded-2xl p-4 text-sm leading-relaxed flex items-start gap-3"
              style={{ background: "var(--primary-tint)", borderLeft: "4px solid var(--primary)" }}
            >
              <Receipt size={18} className="shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
              <span>
                Le montant affiché est une estimation. Le montant final sera confirmé lors de la facturation, en tenant compte d'un éventuel prorata en cas d'inscription en cours de semestre ou de tout autre ajustement spécifique. Pour les cours participant au spectacle de juin, un costume obligatoire de <strong>CHF 40.– à CHF 60.–</strong> par cours sera ajouté à la facture du deuxième semestre (hors groupes compétition).
              </span>
            </div>
          </section>

          {children.length >= 3 && (
            <div className="card-flat text-sm" style={{ background: "#FEF9C3", borderColor: "#FDE68A" }}>
              ⚠️ Tu inscris 3 enfants ou plus — les réductions supplémentaires éventuelles seront confirmées par 1000ARTS.
            </div>
          )}

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="btn-ghost"><ArrowLeft size={14} /> Précédent</button>
            <button onClick={() => setStep(4)} data-testid="step-3-next" className="btn-primary">Valider et signer <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 4: CONTRACT */}
      {step === 4 && (
        <div data-testid="step-4" className="space-y-6">
          <section className="card-flat">
            <div className="font-display text-xl mb-4">Conditions et règlement</div>
            <div className="text-sm text-[var(--text)] space-y-3 mb-5 leading-relaxed">
              <p><strong>Paiement :</strong> Les cours sont à payer par factures distribuées au début de chaque semestre. Délai de paiement : 30 jours. En cas de retard, des frais de rappel seront facturés. L'élève s'engage pour une durée minimale d'un semestre.</p>
              <p>Une réduction de CHF 25.– est accordée à chaque semestre pour le 2ème enfant d'une même famille, vivant sous le même toit.</p>
              <p>Aucun remboursement n'est effectué, en particulier en cas d'arrêt des cours. En cas de non-paiement, nous nous réservons le droit de refuser l'élève aux cours.</p>
            </div>

            {/* ENCART Reconduction automatique — mis en évidence */}
            <div
              data-testid="reconduction-callout"
              className="rounded-2xl p-5 my-5 flex items-start gap-4"
              style={{ background: "#FFF7ED", borderLeft: "4px solid #F97316", color: "#7C2D12" }}
            >
              <span className="text-2xl leading-none shrink-0">⚠️</span>
              <div className="text-[15px] leading-relaxed">
                <strong className="block mb-1 text-[#7C2D12]">Reconduction automatique</strong>
                Sans courrier de désistement <strong>2 mois avant le début du semestre suivant</strong>, le contrat est reconduit tacitement pour le semestre suivant.
              </div>
            </div>

            <div className="text-sm text-[var(--text)] mb-5 leading-relaxed">
              {/* Ouvert dans un nouvel onglet : le formulaire en cours de saisie n'est
                  ainsi jamais perdu. */}
              <a
                href="/reglement"
                target="_blank"
                rel="noreferrer"
                data-testid="reglement-link"
                className="inline-flex items-center gap-1 font-medium"
                style={{ color: "var(--primary)" }}
              >
                <FileText size={14} /> Lire le règlement général
                <ExternalLink size={12} />
              </a>
            </div>

            {hasCompetition && (
              <div
                className="p-5 rounded-2xl mb-5"
                data-testid="reglement-competition-block"
                style={{ background: "#FFF7ED", borderLeft: "4px solid #F97316" }}
              >
                <div className="font-display text-lg mb-2" style={{ color: "#7C2D12" }}>
                  Règlement Compétition &amp; Compagnie — à retourner signé
                </div>
                <p className="text-sm leading-relaxed mb-3" style={{ color: "#7C2D12" }}>
                  Un cours de compétition ou de compagnie figure dans cette inscription. Ces groupes
                  représentent officiellement l'école et sont encadrés par un règlement spécifique
                  (assiduité, ponctualité, esprit d'équipe, frais de compétition et de costumes,
                  cours de chant privés pour la compagnie).
                </p>
                <p className="text-sm leading-relaxed mb-4" style={{ color: "#7C2D12" }}>
                  <strong>Il doit être signé par l'élève ET son représentant légal, puis remis à
                  l'école avant la première participation.</strong> Tu peux le télécharger ci-dessous,
                  l'imprimer, le signer et le rapporter au secrétariat — ou nous le renvoyer scanné
                  par e-mail à <a href="mailto:info1000arts@gmail.com" className="underline">info1000arts@gmail.com</a>.
                </p>
                <a
                  href={REGLEMENT_COMPETITION_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reglement-competition-link"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
                  style={{ background: "#EA580C", color: "#fff" }}
                >
                  <FileText size={15} /> Télécharger le règlement Compétition &amp; Compagnie
                </a>
              </div>
            )}

            <div className="space-y-3 mt-6 pt-6 border-t border-[var(--border-soft)]">
              {TERMS.map((t) => (
                <label key={t.k} className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="checkbox" data-testid={`term-${t.k}`} checked={!!accepted[t.k]} onChange={(e) => setAccepted({ ...accepted, [t.k]: e.target.checked })} className="mt-1 accent-[var(--primary)]" />
                  <span>{t.t}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="card-flat" data-testid="photo-section">
            <div className="font-display text-xl mb-3">Droit à l'image{hasCompetition ? " *" : ""}</div>
            <p className="text-sm text-[var(--muted)] mb-4">
              J'autorise l'utilisation de photos / vidéos à des fins de communication.
              {children.length > 1 && " Une réponse par enfant."}
              {children.some((c) => !enCompetition(c)) && (hasCompetition
                ? " Pour un élève qui n'est pas en compétition, ce choix est facultatif et n'a aucune conséquence sur l'inscription — tu peux le modifier à tout moment."
                : " Ce choix est facultatif et n'a aucune conséquence sur l'inscription — tu peux le modifier à tout moment.")}
            </p>
            <div>
              {children.map((c, idx) => {
                const comp = enCompetition(c);
                const valeur = photoDe(c);
                const nom = `${c.first_name || ""} ${c.last_name || ""}`.trim() || `Enfant ${idx + 1}`;
                return (
                  <div key={idx} data-testid={`photo-enfant-${idx}`}
                    className={children.length > 1
                      ? "py-4 first:pt-0 last:pb-0 border-t first:border-t-0 border-[var(--border-soft)]" : ""}>
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                      {children.length > 1 && (
                        <div className="font-medium">{nom}{comp ? " *" : ""}</div>
                      )}
                      <div className="flex gap-3">
                        {["oui", "non"].map((v) => {
                          const bloque = comp && v === "non";
                          return (
                            <button key={v} type="button" data-testid={`photo-${idx}-${v}`}
                              onClick={() => updateChild(idx, "photo_consent", v)}
                              disabled={bloque} aria-pressed={valeur === v}
                              style={bloque ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                              className={`pill ${valeur === v ? "active" : ""}`}>
                              {v === "oui" ? "Oui" : "Non"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {comp && (
                      <p className="text-sm mt-3" style={{ color: "#B45309" }} data-testid={`photo-competition-note-${idx}`}>
                        {children.length > 1 && <>{c.first_name || nom} est inscrit·e en Compétition ou en Compagnie. </>}
                        Les élèves de compétition sont exposés aux médias, aux réseaux sociaux et aux
                        supports des concours : l'accord à l'image est <strong>obligatoire</strong> pour
                        une inscription en Compétition ou en Compagnie.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="card-flat">
            <div className="font-display text-xl mb-3">Autres commentaires</div>
            <p className="text-sm text-[var(--muted)] mb-3">
              Une allergie, un souhait de groupe, une information utile pour le professeur ?
              Écris-le ici, l'école le lira avec l'inscription.
            </p>
            <textarea data-testid="sem-comments" rows={3} className="input-field"
              value={comments} onChange={(e) => setComments(e.target.value)} />
          </section>

          <section className="card-flat">
            <div className="font-display text-xl mb-4">Signature électronique</div>
            <div>
              <label className="field-label">Nom complet du signataire *</label>
              <input data-testid="signature-name" className="input-field" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Ex : Marie Dupont" />
            </div>
            <label className="flex items-start gap-3 text-sm cursor-pointer mt-4">
              <input type="checkbox" data-testid="signature-confirm" checked={signatureValid} onChange={(e) => setSignatureValid(e.target.checked)} className="mt-1 accent-[var(--primary)]" />
              <span>Je confirme que ce nom fait office de signature électronique pour cette inscription.</span>
            </label>
          </section>

          {error && <div data-testid="sem-error" className="text-sm" style={{ color: "#DC2626" }}>{error}</div>}

          <div className="flex justify-between gap-3">
            <button onClick={() => setStep(3)} className="btn-ghost"><ArrowLeft size={14} /> Précédent</button>
            <button onClick={submit} disabled={!termsValid || loading} data-testid="sem-submit" className="btn-primary">
              {loading ? "Envoi…" : "Valider l'inscription"} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================
// STAGE FORM — générique (liste de stages depuis pricing.js / Google Sheet Phase 2)
// =========================================================
function StageForm({ onBack }) {
  // Pré-sélectionne le premier stage OUVERT, sinon le premier de la liste.
  const defaultStage = STAGES.find((s) => !stageFerme(s)) || STAGES[0];
  const [form, setForm] = useState({
    parent_first_name: "", parent_last_name: "", parent_email: "", parent_phone: "",
    child_first_name: "", child_last_name: "", child_birth: "", address: "", npa: "", city: "",
    first_registration: true,
    stage_id: defaultStage.id, option_id: "", comments: "", discipline_preference: "", photo_consent: "oui", signature: "",
  });
  const famille = useFamilleConnectee();
  useEffect(() => {
    if (!famille) return;
    const c = coordonneesDe(famille);
    setForm((f) => ({
      ...f,
      parent_first_name: f.parent_first_name || c.prenom, parent_last_name: f.parent_last_name || c.nom,
      parent_email: f.parent_email || c.email, parent_phone: f.parent_phone || formatSwissPhone(c.telephone),
      address: f.address || c.adresse, npa: f.npa || c.npa, city: f.city || c.localite,
    }));
  }, [famille]);
  const [accepted, setAccepted] = useState({});
  const [signatureValid, setSignatureValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const stage = STAGES.find((s) => s.id === form.stage_id) || defaultStage;
  // Fermé aussi s'il a commencé ou s'il est passé (date LOCALE), même si la console ne
  // répond pas : on ne s'inscrit jamais à un stage en cours ou terminé.
  const stageClosed = stageFerme(stage);
  const options = stage.options || [];
  const option = options.find((o) => o.id === form.option_id);

  const changeStage = (id) => {
    setForm((f) => ({ ...f, stage_id: id, option_id: "" }));
  };

  const TERMS = [
    { k: "all_conditions", t: "J'ai lu et j'accepte les conditions ci-dessus." },
  ];
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSubmit =
    !stageClosed &&
    form.parent_first_name && form.parent_last_name && isValidEmail(form.parent_email) && form.parent_phone &&
    form.address && form.npa && form.city &&
    form.child_first_name && form.child_last_name &&
    // Date de naissance marquée * : obligatoire et plausible (elle fixe le groupe d'âge).
    form.child_birth && !birthDateError(form.child_birth) && form.option_id &&
    TERMS.every((t) => accepted[t.k]) && signatureValid && form.signature.trim().length > 1;

  const sending = useRef(false);
  const submit = async () => {
    if (sending.current) return;
    sending.current = true;
    setLoading(true); setError("");
    try {
      await api.post("/inscriptions/stage", {
        ...form,
        accepted, signature_valid: signatureValid,
        stage: { id: stage.id, sheet_id: stage.sheet_id, title: stage.title, dates_label: stage.dates_label, hours_label: stage.hours_label, location_label: stage.location_label },
        option,
      });
      setSuccess(true);
    } catch (e) {
      setError(messageErreurEnvoi(e));
    } finally { setLoading(false); sending.current = false; }
  };

  if (success) {
    return (
      <div data-testid="stage-success" className="text-center py-12">
        <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "var(--primary)" }}>
          <Check size={28} color="#fff" />
        </div>
        <h2 className="font-display text-3xl md:text-4xl">Merci, ta demande d'inscription au stage a bien été envoyée.</h2>
        <p className="text-[var(--muted)] mt-4 max-w-lg mx-auto">1000ARTS te confirmera la disponibilité.</p>
      </div>
    );
  }

  return (
    <div data-testid="stage-form" className="space-y-8">
      <button onClick={onBack} data-testid="stage-back" className="btn-ghost"><ArrowLeft size={14} /> Retour</button>

      <div>
        <div className="eyebrow mb-2">Inscription Stage</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight">{stage.title}</h2>
        <p className="text-[var(--muted)] mt-2">{stage.dates_label} · {stage.ages_label}</p>
      </div>

      {/* SÉLECTION DU STAGE */}
      <section className="card-flat" data-testid="stage-picker">
        <div className="font-display text-xl mb-3">Quel stage&nbsp;?</div>
        <p className="text-sm text-[var(--muted)] mb-4">
          Choisis le stage auquel tu souhaites inscrire ton enfant. La liste est mise à jour automatiquement à chaque nouveau stage proposé par 1000 ARTS.
        </p>
        <div className="space-y-2">
          {STAGES.map((s) => {
            const closed = stageFerme(s);
            const active = form.stage_id === s.id;
            return (
              <label
                key={s.id}
                data-testid={`stage-choice-${s.id}`}
                className={`flex items-start justify-between gap-3 p-4 rounded-2xl ${closed ? "cursor-not-allowed" : "cursor-pointer"}`}
                style={{
                  background: active ? "var(--primary-tint)" : "var(--bg-soft)",
                  border: `1.5px solid ${active ? "var(--primary)" : "transparent"}`,
                  opacity: closed ? 0.65 : 1,
                }}
                aria-disabled={closed}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <input
                    type="radio"
                    name="stage"
                    className="mt-1 accent-[var(--primary)]"
                    checked={active}
                    disabled={closed}
                    onChange={() => !closed && changeStage(s.id)}
                    data-testid={`stage-radio-${s.id}`}
                  />
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      <span>{s.title}</span>
                      {closed && (
                        <span
                          className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                          style={{ background: "#E5E7EB", color: "#4B5563" }}
                        >
                          Inscriptions fermées
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[var(--muted)] mt-0.5">{s.dates_label} · {s.ages_label}</div>
                    {closed && s.status_label && (
                      <div className="text-xs mt-1" style={{ color: "#6B7280" }}>{s.status_label}</div>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        {stageClosed && (
          <div
            data-testid="stage-closed-banner"
            className="mt-4 rounded-2xl p-4 flex items-start gap-3"
            style={{ background: "#F3F4F6", borderLeft: "4px solid #9CA3AF", color: "#374151" }}
          >
            <span className="text-xl leading-none">ℹ️</span>
            <div className="text-sm leading-relaxed">
              Ce stage est <strong>terminé</strong> — les inscriptions sont fermées. Consulte la <Link to="/stages" className="underline font-medium">page Stages</Link> pour découvrir les prochains stages ou choisis un autre stage dans la liste ci-dessus.
            </div>
          </div>
        )}
      </section>

      <BandeauConnecte moi={famille} texte="Tes coordonnées sont déjà remplies." />

      <section className="card-flat">
        <div className="font-display text-xl mb-5">Représentant légal</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="field-label">Prénom *</label><input data-testid="stage-parent-first" className="input-field" value={form.parent_first_name} onChange={update("parent_first_name")} disabled={stageClosed} /></div>
          <div><label className="field-label">Nom *</label><input data-testid="stage-parent-last" className="input-field" value={form.parent_last_name} onChange={update("parent_last_name")} disabled={stageClosed} /></div>
          <div><label className="field-label">E-mail *</label><input data-testid="stage-parent-email" type="email" className="input-field" value={form.parent_email} onChange={update("parent_email")} disabled={stageClosed} /><EmailSuggestion value={form.parent_email} onFix={(v) => setForm({ ...form, parent_email: v })} testid="stage-email-suggestion" /></div>
          <div><label className="field-label">Téléphone *</label><input data-testid="stage-parent-phone" type="tel" inputMode="tel" placeholder="+41 79 950 40 31" className="input-field" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: formatSwissPhone(e.target.value) })} disabled={stageClosed} /></div>
          <div className="md:col-span-2"><label className="field-label">Adresse *</label><input data-testid="stage-address" className="input-field" placeholder="Rue et numéro" value={form.address} onChange={update("address")} disabled={stageClosed} /></div>
          <div>
            <label className="field-label">NPA *</label>
            <input
              data-testid="stage-npa"
              className="input-field"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              placeholder="1053"
              value={form.npa}
              onChange={(e) => {
                const npa = e.target.value.replace(/\D/g, "").slice(0, 4);
                const suggested = cityFromNpa(npa);
                setForm((f) => ({
                  ...f,
                  npa,
                  city: suggested && isAutoFilledCity(f.city) ? suggested : f.city,
                }));
              }}
              disabled={stageClosed}
            />
          </div>
          <div>
            <label className="field-label">Localité *</label>
            <input data-testid="stage-city" className="input-field" placeholder="Cugy VD" value={form.city} onChange={update("city")} disabled={stageClosed} />
          </div>
        </div>
      </section>

      <section className="card-flat">
        <div className="font-display text-xl mb-5">Enfant</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="field-label">Prénom *</label><input data-testid="stage-child-first" className="input-field" value={form.child_first_name} onChange={update("child_first_name")} disabled={stageClosed} /></div>
          <div><label className="field-label">Nom *</label><input data-testid="stage-child-last" className="input-field" value={form.child_last_name} onChange={update("child_last_name")} disabled={stageClosed} /></div>
          <div><label className="field-label">Date de naissance *</label><ChampNaissance testid="stage-child-birth" value={form.child_birth} onChange={(v) => setForm((f) => ({ ...f, child_birth: v }))} disabled={stageClosed} />
            {birthDateError(form.child_birth) && (
              <p data-testid="stage-child-birth-error" className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }}>{birthDateError(form.child_birth)}</p>
            )}
          </div>
        </div>
      </section>

      <section className="card-flat">
        <div className="font-display text-xl mb-5">Tarif / option</div>
        {options.length === 0 && (
          <p className="text-sm text-[var(--muted)]">Aucune option disponible pour ce stage.</p>
        )}
        <div className="space-y-2">
          {options.map((o) => (
            <label key={o.id} className={`flex items-center justify-between p-4 rounded-2xl ${stageClosed ? "cursor-not-allowed" : "cursor-pointer"}`} style={{ background: form.option_id === o.id ? "var(--primary-tint)" : "var(--bg-soft)", border: `1.5px solid ${form.option_id === o.id ? "var(--primary)" : "transparent"}`, opacity: stageClosed ? 0.6 : 1 }}>
              <div className="flex items-center gap-3">
                <input type="radio" name="option" data-testid={`stage-option-${o.id}`} checked={form.option_id === o.id} onChange={() => setForm({ ...form, option_id: o.id })} className="accent-[var(--primary)]" disabled={stageClosed} />
                <span className="font-medium">{o.label}</span>
              </div>
              <span className="font-display text-xl">CHF {o.price}.–</span>
            </label>
          ))}
        </div>

        {option && (
          <div className="mt-6 pt-5 border-t border-[var(--border-soft)] flex justify-between items-baseline">
            <span className="font-display text-lg">Total estimé</span>
            <span className="font-display text-3xl" style={{ color: "var(--primary)" }}>CHF {option.price}.–</span>
          </div>
        )}
        <p className="text-xs text-[var(--muted)] italic mt-4">Le montant final sera vérifié et confirmé par 1000ARTS lors de la facturation.</p>

        <div className="mt-5">
          <label className="field-label">Préférence de discipline / souhaits particuliers <span className="text-[var(--muted)] font-normal">(facultatif)</span></label>
          <textarea
            data-testid="stage-discipline-preference"
            rows={3}
            className="input-field"
            value={form.discipline_preference}
            onChange={update("discipline_preference")}
            disabled={stageClosed}
            placeholder="Ex : mon enfant préfère la danse, il aimerait aussi essayer le chant, être dans le même groupe qu'un·e ami·e…"
          />
          <p className="text-xs text-[var(--muted)] italic mt-2">
            Si ton enfant a une préférence entre <strong>danse</strong>, <strong>chant</strong> ou <strong>théâtre</strong>, ou tout autre souhait particulier, indique-le ici. Nous en tenons compte dans la mesure du possible pour former les groupes, sans pouvoir toutefois le garantir.
          </p>
        </div>

        <div className="mt-5">
          <label className="field-label">Autres commentaires</label>
          <textarea data-testid="stage-comments" rows={3} className="input-field" value={form.comments} onChange={update("comments")} disabled={stageClosed} />
        </div>
      </section>

      <section className="card-flat">
        <div className="font-display text-xl mb-3">Confirmation d'inscription</div>
        <p className="text-sm text-[var(--text)] leading-relaxed mb-5">
          En validant ce formulaire, je confirme souhaiter inscrire mon enfant au stage sélectionné. Je comprends que l'inscription sera confirmée par 1000ARTS en fonction des disponibilités. Je certifie que les informations transmises sont exactes et confirme être le représentant légal de l'enfant inscrit.
        </p>
        <div className="space-y-3">
          {TERMS.map((t) => (
            <label key={t.k} className="flex items-start gap-3 text-sm cursor-pointer">
              <input type="checkbox" data-testid={`stage-term-${t.k}`} checked={!!accepted[t.k]} onChange={(e) => setAccepted({ ...accepted, [t.k]: e.target.checked })} className="mt-1 accent-[var(--primary)]" disabled={stageClosed} />
              <span>{t.t}</span>
            </label>
          ))}
        </div>

        <div className="mt-5 pt-5 border-t border-[var(--border-soft)]">
          <div className="font-medium mb-3">Droit à l'image</div>
          <div className="flex gap-3">
            {["oui", "non"].map((v) => (
              <button key={v} data-testid={`stage-photo-${v}`} onClick={() => setForm({ ...form, photo_consent: v })} className={`pill ${form.photo_consent === v ? "active" : ""}`} disabled={stageClosed}>{v === "oui" ? "Oui" : "Non"}</button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <label className="field-label">Signature électronique — Nom complet *</label>
          <input data-testid="stage-signature" className="input-field" value={form.signature} onChange={update("signature")} disabled={stageClosed} />
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
            <input type="checkbox" data-testid="stage-signature-confirm" checked={signatureValid} onChange={(e) => setSignatureValid(e.target.checked)} className="accent-[var(--primary)]" disabled={stageClosed} />
            Je confirme que ce nom fait office de signature électronique pour cette inscription.
          </label>
        </div>
      </section>

      {error && <div data-testid="stage-error" className="text-sm" style={{ color: "#DC2626" }}>{error}</div>}

      <button data-testid="stage-submit" onClick={submit} disabled={!canSubmit || loading} className="btn-primary w-full justify-center">
        {loading ? "Envoi…" : (stageClosed ? "Inscriptions fermées" : "Envoyer ma demande")} <ArrowRight size={16} />
      </button>
    </div>
  );
}

// =========================================================
// MAIN PAGE
// =========================================================
export default function Inscription() {
  useSEO({
    title: "Inscription en ligne — 1000 ARTS Cugy VD",
    description: "Inscris-toi en ligne à 1000 ARTS : cours d'essai, inscription semestrielle multi-enfants avec calcul automatique des prix, inscription stage. Rabais famille et rabais accumulation.",
    canonical: "/inscription",
  });
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const initial = params.get("type") || null;
  const preselectedCourse = params.get("course") || null;
  const [type, setType] = useState(initial);

  // Sync `type` avec l'URL : quand l'utilisateur clique sur "Inscription" dans la nav
  // (URL redevient /inscription sans ?type=), on remet le sélecteur de type.
  // Dépendance sur location.search car les objets useSearchParams peuvent garder la même
  // référence même quand l'URL change.
  React.useEffect(() => {
    const urlType = new URLSearchParams(location.search).get("type") || null;
    if (urlType !== type) {
      setType(urlType);
      if (!urlType) window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.key]);

  const goBackToMenu = () => {
    setType(null);
    setParams({}); // clear ?type= from URL
    window.scrollTo(0, 0);
  };

  return (
    <div data-testid="page-inscription" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
      <div className="eyebrow mb-3">Inscription</div>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-tight mb-12">
        Rejoignez<br /><span style={{ color: "var(--primary)" }}>1000 ARTS</span>
      </h1>

      {!type && <TypeSelector onSelect={setType} />}
      {type === "essai" && <EssaiForm onBack={goBackToMenu} preselectedCourse={preselectedCourse} />}
      {type === "semestriel" && <SemestrielForm onBack={goBackToMenu} preselectedCourse={preselectedCourse} />}
      {type === "stage" && <StageForm onBack={goBackToMenu} />}
      </div>
    </div>
  );
}
